import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BookieAddSideEventModal from './BookieAddSideEventModal';
import BookieEditSidePotModal from './BookieEditSidePotModal';
import BookieScoreEntry from './BookieScoreEntry';
import BookiePayoutModal from './BookiePayoutModal';
import BookieHandicapsModal from './BookieHandicapsModal';
import QuickEntriesModal from './QuickEntriesModal';
import SideEventPayoutPresets from '../SideEventPayoutPresets';
import QuickBracketEntriesModal from './QuickBracketEntriesModal';
import AddBowlerModal from './AddBowlerModal';
import { countries } from '../../data/countries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* ─── Types ─────────────────────────────────────────────────────────────── */
interface League { id: number; name: string; venue: string; start_date: string; end_date: string; hidden?: number; }
interface Squad { id: number; name: string; date: string; }
interface SideEvent {
  id: number; squad_id: number; league_id: number; name: string; type: string; applies_to: string;
  cost: number; is_active: number; applies_to_game_numbers: string;
  bracket_size?: number; entry_count: number;
  gender?: string | null; min_age?: number | null; max_age?: number | null; hdcp?: boolean;
}
interface Entry { id: number; bowler_id: number; bowler_name: string; email: string; paid: number; }
interface BracketEntry { bowler_id: number; name: string; entryCount: number; }
interface Bowler { id: number; name: string; email: string; }
interface BracketMatchupData {
  id: number; matchup_number: number;
  entry1_id: number | null; entry1_name: string | null; entry1_bowler_id: number | null; entry1_score: number | null;
  entry2_id: number | null; entry2_name: string | null; entry2_bowler_id: number | null; entry2_score: number | null;
  winner_entry_id: number | null; winner_name: string | null; is_bye: boolean;
}
interface BracketRoundData { id: number; round_number: number; game_number: number; matchups: BracketMatchupData[]; }
interface BracketData { id: number; bracket_number: number; rounds: BracketRoundData[]; }
interface Financial {
  side_event_id: number; name: string; type: string; cost: number; applies_to: string;
  squad_name: string; squad_date: string; total_entries: number; paid_entries: number; total_pot: number;
}
interface Payout {
  id: number; side_event_id: number; payout_date: string; recipient_type: string;
  recipient_id: number; amount: number; placement: string | null; notes: string | null;
  checkmark: number;
  se_name: string; se_type: string; squad_id: number; squad_name: string; squad_date: string;
  recipient_name: string | null;
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const fmtDate = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const TYPE_LABELS: Record<string, string> = {
  high_game: 'High Game', high_series: 'High Series', mystery_score: 'Mystery Score',
  mystery_series: 'Mystery Series', side_pots: 'Side Pots', survivor: 'Survivor',
  eliminator: 'Eliminator', clean_game: 'Clean Game', bracket: 'Bracket', custom: 'All with All',
};

/* ─── Print Winners helper ─────────────────────────────────────────────── */
const PAID_BADGE = '<span style="display:inline-block;background:#fef3c7;color:#92400e;font-size:9px;font-weight:600;padding:1px 5px;border-radius:3px;margin-left:5px;">✓ PAID</span>';

function buildWinnersHTML(seResult: any, paidIds?: Set<number>): string {
  const type = seResult.type;
  const paidBadge = (id: number) => paidIds?.has(id) ? PAID_BADGE : '';

  // Survivor: full rounds table matching the individual print view
  if (type === 'survivor') {
    const rounds: any[] = seResult.computed_rounds || [];
    if (rounds.length === 0) return '<em style="color:#9ca3af;">No rounds data yet</em>';
    const rowsHTML = rounds.map((r: any) => {
      const survived = (r.survivor_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') || '—';
      const elim = (r.eliminated_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') || '—';
      return `<tr>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Game ${r.game_number}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Min Score: <strong>${r.survivor_score}</strong></td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#15803d;">${survived}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#b91c1c;">${elim}</td>
      </tr>`;
    }).join('');
    const lastRound = rounds[rounds.length - 1];
    const survivorScoreMap: Record<number, number> = {};
    for (const b of (lastRound?.survivor_list || [])) {
      survivorScoreMap[b.bowler_id] = b.score;
    }
    const finalCashers = (seResult.cashers || []).length === 0 ? 'None yet' :
      [...(seResult.cashers || [])]
        .sort((a: any, b: any) => (survivorScoreMap[b.bowler_id] || 0) - (survivorScoreMap[a.bowler_id] || 0))
        .map((c: any) => `${c.bowler_name} (${survivorScoreMap[c.bowler_id] ?? '—'})${paidBadge(c.bowler_id)}`)
        .join(', ');
    return `<table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead><tr>
        <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Game</th>
        <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Threshold</th>
        <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;color:#15803d;">Survived</th>
        <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;color:#b91c1c;">Eliminated</th>
      </tr></thead>
      <tbody>${rowsHTML}</tbody>
    </table>
    <div style="margin-top:10px;padding:8px 12px;background:#fefce8;border:1px solid #ca8a04;border-radius:6px;font-size:12px;">
      <strong>Finalists:</strong> ${finalCashers}
    </div>`;
  }

  // Eliminator: full rounds table matching the individual print view
  if (type === 'eliminator') {
    const rounds: any[] = seResult.computed_rounds || [];
    if (rounds.length === 0) return '<em style="color:#9ca3af;">No rounds data yet</em>';
    const rowsHTML = rounds.map((r: any) => {
      const advancing = (r.advancer_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') || '—';
      const elim = (r.eliminated_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') || '—';
      return `<tr>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Game ${r.game_number}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Advance Top: <strong>${r.advancement_percentage}%</strong></td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#15803d;">${advancing}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#b91c1c;">${elim}</td>
      </tr>`;
    }).join('');
    const lastElimRound = rounds[rounds.length - 1];
    const elimScoreMap: Record<number, number> = {};
    for (const b of (lastElimRound?.advancer_list || [])) {
      elimScoreMap[b.bowler_id] = b.score;
    }
    const finalCashers = (seResult.cashers || []).length === 0 ? 'None yet' :
      [...(seResult.cashers || [])]
        .sort((a: any, b: any) => (elimScoreMap[b.bowler_id] || 0) - (elimScoreMap[a.bowler_id] || 0))
        .map((c: any) => `${c.bowler_name} (${elimScoreMap[c.bowler_id] ?? '—'})${paidBadge(c.bowler_id)}`)
        .join(', ');
    return `<table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead><tr>
        <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Game</th>
        <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Threshold</th>
        <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;color:#15803d;">Advancing</th>
        <th style="background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;color:#b91c1c;">Eliminated</th>
      </tr></thead>
      <tbody>${rowsHTML}</tbody>
    </table>
    <div style="margin-top:10px;padding:8px 12px;background:#fefce8;border:1px solid #ca8a04;border-radius:6px;font-size:12px;">
      <strong>Finalists:</strong> ${finalCashers}
    </div>`;
  }

  // Side pots (also used for All with All / custom)
  if (type === 'side_pots' || type === 'custom') {
    const pots: any[] = seResult.pot_results || [];
    if (pots.length === 0) return '<em style="color:#9ca3af;">No scores entered</em>';
    return pots.map((pot: any) => {
      const label = pot.config_type === 'game_series' ? `Series (G${pot.game_identifier})` : `Game ${pot.game_identifier}`;
      const all: any[] = pot.all_entries || pot.winners || [];
      const rows = all.map((b: any, i: number) => {
        const isWinner = i < pot.winner_count;
        const isPaid = paidIds && (pot.is_doubles
          ? (b.bowler_ids || []).some((bi: any) => paidIds.has(bi.bowler_id))
          : paidIds.has(b.bowler_id));
        return `<div style="padding:2px 0;${isWinner ? 'color:#15803d;font-weight:600;' : ''}">${i + 1}. ${b.bowler_name} — ${b.score}${isWinner ? ' ✓' : ''}${isPaid ? PAID_BADGE : ''}</div>`;
      }).join('');
      return `<div style="margin-bottom:10px;">
        <strong style="font-size:11px;color:#6b7280;">${label}</strong>
        <div style="margin-top:3px;font-size:12px;">${rows || '<em style="color:#9ca3af;">No scores</em>'}</div>
      </div>`;
    }).join('');
  }

  // High game / high series
  const standings: any[] = seResult.standings || [];
  if (standings.length === 0) return '<em style="color:#9ca3af;">No scores entered</em>';
  return standings.map((s: any, i: number) => {
    const score = type === 'high_series' ? `${s.total_score} (series)` : `${s.best_game}`;
    return `<div style="padding:2px 0;">${i + 1}. ${s.bowler_name} — ${score}${paidBadge(s.bowler_id)}</div>`;
  }).join('');
}

// Types to skip entirely on print results
const SKIP_PRINT_TYPES = new Set(['mystery_score', 'mystery_series', 'clean_game', 'bracket', 'custom']);

function printWinners(leagueName: string, scopeLabel: string, seResults: any[], paidMap?: Map<number, Set<number>>) {
  const printable = seResults.filter(se => !SKIP_PRINT_TYPES.has(se.type));
  if (printable.length === 0) { alert('No printable side event data for this scope.'); return; }
  const seSections = printable.map(se => {
    const typeLabel = TYPE_LABELS[se.type] || se.type;
    return `<div style="margin-bottom:24px;page-break-inside:avoid;">
      <h3 style="margin:0 0 6px;font-size:14px;">${se.name} <span style="color:#6b7280;font-weight:400;font-size:12px;">(${typeLabel})</span></h3>
      <div style="font-size:13px;line-height:1.7;">${buildWinnersHTML(se, paidMap?.get(se.id))}</div>
    </div>`;
  }).join('<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;"/>');

  const html = `<!DOCTYPE html><html><head><title>Results – ${leagueName}</title>
    <style>body{font-family:sans-serif;padding:24px;color:#111;}table{width:100%;border-collapse:collapse;}th{text-align:left;}</style>
    </head><body>
    <h2 style="margin-bottom:4px;">Results – ${leagueName}</h2>
    <p style="color:#6b7280;font-size:13px;margin-bottom:20px;">${scopeLabel}</p>
    ${seSections}
    <p style="color:#9ca3af;font-size:11px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=820,height=700');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

/* ─── Print Payments helper ────────────────────────────────────────────── */
function printPayments(leagueName: string, scopeLabel: string, financials: Financial[]) {
  const squadHeader = scopeLabel === 'All Squads' ? '<th style="padding:8px 12px;background:#f3f4f6;text-align:left;">Squad</th>' : '';
  const rows = financials.map(f => `
    <tr>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${f.name}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${TYPE_LABELS[f.type] || f.type}</td>
      ${scopeLabel === 'All Squads' ? `<td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${f.squad_name || ''} (${f.squad_date ? fmtDate(f.squad_date) : ''})</td>` : ''}
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center;">${f.total_entries}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">$${Number(f.cost).toFixed(2)}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:bold;">$${Number(f.total_pot).toFixed(2)}</td>
    </tr>`).join('');
  const totalPot = financials.reduce((s, f) => s + Number(f.total_pot), 0);
  const colCount = scopeLabel === 'All Squads' ? 6 : 5;
  const html = `<!DOCTYPE html><html><head><title>Payments – ${leagueName}</title>
    <style>body{font-family:sans-serif;padding:24px;color:#111;}table{width:100%;border-collapse:collapse;}th{background:#f3f4f6;padding:8px 12px;text-align:left;border-bottom:2px solid #d1d5db;}</style>
    </head><body>
    <h2 style="margin-bottom:4px;">Payments by Bowlers – ${leagueName}</h2>
    <p style="color:#6b7280;font-size:14px;margin-bottom:20px;">Scope: ${scopeLabel}</p>
    <table><thead><tr>
      <th>Side Event</th><th>Type</th>${squadHeader}
      <th style="text-align:center;">Entries</th>
      <th style="text-align:right;">Cost</th>
      <th style="text-align:right;">Total Pot</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="${colCount - 1}" style="padding:10px 12px;font-weight:bold;text-align:right;border-top:2px solid #111;">Total</td>
      <td style="padding:10px 12px;font-weight:bold;text-align:right;border-top:2px solid #111;">$${totalPot.toFixed(2)}</td>
    </tr></tfoot></table>
    <p style="color:#9ca3af;font-size:12px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=820,height=600');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

/* ─── Print Payouts helper ─────────────────────────────────────────────── */
function printPayouts(leagueName: string, scopeLabel: string, payouts: Payout[]) {
  const squadHeader = scopeLabel === 'All Squads' ? '<th style="padding:8px 12px;background:#f3f4f6;text-align:left;">Squad</th>' : '';

  // Group payouts by recipient, sort by last name
  const map = new Map<string, Payout[]>();
  for (const p of payouts) {
    const key = p.recipient_name || `#${p.recipient_id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }
  const groups = Array.from(map.entries()).map(([name, rows]) => ({
    name,
    rows: [...rows.filter((r: Payout) => r.se_type === 'bracket'), ...rows.filter((r: Payout) => r.se_type !== 'bracket')],
    total: rows.reduce((s: number, r: Payout) => s + Number(r.amount), 0),
  })).sort((a, b) => (_lastName(a.name)).localeCompare(_lastName(b.name)));

  const colCount = scopeLabel === 'All Squads' ? 7 : 6;
  let rows = '';
  let grandTotal = 0;
  let firstGroup = true;
  for (const g of groups) {
    const multi = g.rows.length > 1;
    const rowBg = multi ? 'background:#eef2ff;' : '';
    const groupSep = firstGroup ? '' : 'border-top:2px solid #9ca3af;';
    firstGroup = false;
    let isFirstRow = true;
    for (const p of g.rows) {
      const sep = isFirstRow ? groupSep : 'border-top:1px solid #e5e7eb;';
      const nameCell = isFirstRow ? _fmtName(g.name) : '<span style="color:#d1d5db;padding-left:12px;">└─</span>';
      isFirstRow = false;
      rows += `<tr style="${rowBg}${sep}">
        <td style="padding:6px 12px;border-bottom:1px solid #e5e7eb;">${nameCell}</td>
        <td style="padding:6px 12px;border-bottom:1px solid #e5e7eb;">${p.se_name}</td>
        <td style="padding:6px 12px;border-bottom:1px solid #e5e7eb;">${TYPE_LABELS[p.se_type] || p.se_type}</td>
        ${scopeLabel === 'All Squads' ? `<td style="padding:6px 12px;border-bottom:1px solid #e5e7eb;">${p.squad_name || ''} (${p.squad_date ? fmtDate(p.squad_date) : ''})</td>` : ''}
        <td style="padding:6px 12px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:bold;">$${Number(p.amount).toFixed(2)}</td>
        <td style="padding:6px 12px;border-bottom:1px solid #e5e7eb;">${p.payout_date ? new Date(p.payout_date).toLocaleDateString() : ''}</td>
        <td style="padding:6px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">${p.notes || ''}</td>
      </tr>`;
    }
    if (multi) {
      rows += `<tr style="background:#f0fdf4;">
        <td colspan="${colCount - 1}" style="padding:5px 12px;text-align:right;font-weight:600;font-size:12px;border-bottom:2px solid #86efac;border-top:1px solid #86efac;color:#166534;">Subtotal — ${_fmtName(g.name)}</td>
        <td style="padding:5px 12px;text-align:right;font-weight:700;border-bottom:2px solid #86efac;border-top:1px solid #86efac;color:#166534;">$${g.total.toFixed(2)}</td>
      </tr>`;
    }
    grandTotal += g.total;
  }

  const html = `<!DOCTYPE html><html><head><title>Payouts – ${leagueName}</title>
    <style>body{font-family:sans-serif;padding:24px;color:#111;}table{width:100%;border-collapse:collapse;}th{background:#f3f4f6;padding:8px 12px;text-align:left;border-bottom:2px solid #d1d5db;}</style>
    </head><body>
    <h2 style="margin-bottom:4px;">Payouts to Bowlers – ${leagueName}</h2>
    <p style="color:#6b7280;font-size:14px;margin-bottom:20px;">Scope: ${scopeLabel}</p>
    <table><thead><tr>
      <th>Recipient</th><th>Side Event</th><th>Type</th>${squadHeader}
      <th style="text-align:right;">Amount</th>
      <th>Date</th><th>Notes</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="${colCount - 1}" style="padding:10px 12px;font-weight:bold;text-align:right;border-top:2px solid #111;">Total Paid Out</td>
      <td style="padding:10px 12px;font-weight:bold;text-align:right;border-top:2px solid #111;">$${grandTotal.toFixed(2)}</td>
    </tr></tfoot></table>
    <p style="color:#9ca3af;font-size:12px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=900,height=600');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

/* ─── Print Brackets helper ───────────────────────────────────────────────────────── */
function printBrackets(seName: string, brackets: BracketData[], totalRounds: number, cost: number) {
  const buildMatchupHtml = (m: BracketMatchupData | undefined): string => {
    if (!m) return '<table class="mu"><tr><td class="mu-name"><em class="tbd">TBD</em></td><td class="mu-score"></td></tr><tr><td class="mu-name"><em class="tbd">TBD</em></td><td class="mu-score"></td></tr></table>';
    const e1Won = m.winner_entry_id === m.entry1_id;
    const e2Won = m.winner_entry_id === m.entry2_id;
    const r1 = `<tr style="background:${e1Won ? '#d1fae5' : 'white'}"><td class="mu-name" style="font-weight:${e1Won ? '700' : 'normal'}">${m.entry1_name || '<em class="tbd">TBD</em>'}</td><td class="mu-score" style="font-weight:${e1Won ? '700' : 'normal'}">${m.entry1_score != null ? m.entry1_score : ''}</td></tr>`;
    const r2 = `<tr style="background:${e2Won ? '#d1fae5' : 'white'}"><td class="mu-name" style="font-weight:${e2Won ? '700' : 'normal'}">${m.is_bye ? '<em class="tbd">BYE</em>' : (m.entry2_name || '<em class="tbd">TBD</em>')}</td><td class="mu-score" style="font-weight:${e2Won ? '700' : 'normal'}">${!m.is_bye && m.entry2_score != null ? m.entry2_score : ''}</td></tr>`;
    return `<table class="mu"><tbody>${r1}${r2}</tbody></table>`;
  };

  const bracketHtml = brackets.map(b => {
    const cols = Array.from({ length: totalRounds }, (_, ri) => {
      const roundNum = ri + 1;
      const round = b.rounds.find(r => r.round_number === roundNum);
      const slotCount = Math.pow(2, totalRounds - roundNum);
      const slotH = Math.pow(2, roundNum - 1) * 40;
      const header = roundNum === totalRounds ? 'Final' : `R${roundNum}${round ? ` G${round.game_number}` : ''}`;
      const cells = Array.from({ length: slotCount }, (_, mi) => {
        const matchup = round?.matchups?.[mi];
        return `<div class="slot" style="height:${slotH}px">${buildMatchupHtml(matchup)}</div>`;
      }).join('');
      return `<div class="col"><div class="col-hdr">${header}</div>${cells}</div>`;
    }).join('');
    return `<div class="bracket"><div class="b-title">Bracket #${b.bracket_number}</div><div class="b-rounds">${cols}</div></div>`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><title>Brackets \u2013 ${seName}</title><style>
    @page{size:A4 portrait;margin:12mm}
    body{font-family:Arial,sans-serif;font-size:10px;color:#111;margin:0}
    h2{font-size:14px;margin:0 0 2px} .sub{color:#6b7280;font-size:10px;margin:0 0 10px}
    .page-columns{columns:2;column-gap:6mm}
    .bracket{break-inside:avoid;-webkit-column-break-inside:avoid;border:1px solid #d1d5db;border-radius:4px;padding:5px;margin-bottom:8px}
    .b-title{font-weight:700;font-size:10px;margin-bottom:4px}
    .b-rounds{display:flex;gap:4px}
    .col{flex:0 0 auto}
    .col-hdr{text-align:center;font-size:8px;color:#6b7280;border-bottom:1px solid #e5e7eb;padding-bottom:2px;margin-bottom:2px}
    .slot{display:flex;align-items:center}
    table.mu{width:82px;border-collapse:collapse;border:1px solid #9ca3af}
    table.mu td{border-bottom:1px solid #d1d5db;padding:1px 3px}
    table.mu tr:last-child td{border-bottom:none}
    .mu-name{max-width:62px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:7.5px}
    .mu-score{width:18px;text-align:right;font-variant-numeric:tabular-nums}
    .tbd{color:#9ca3af;font-size:8px}
    .footer{color:#9ca3af;font-size:9px;margin-top:10px}
  </style></head><body>
    <h2>Brackets \u2013 ${seName}</h2>
    <p class="sub">Entry: $${Number(cost).toFixed(2)} &bull; ${brackets.length} bracket${brackets.length !== 1 ? 's' : ''} &bull; ${totalRounds} rounds</p>
    <div class="page-columns">${bracketHtml}</div>
    <p class="footer">Printed ${new Date().toLocaleString()}</p>
  </body></html>`;
  const w = window.open('', '_blank', 'width=820,height=1060');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}
const _lastName = (fullName: string) => fullName.trim().split(/\s+/).pop()?.toLowerCase() ?? '';
const _fmtName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  const last = parts.pop()!;
  return `${last}, ${parts.join(' ')}`;
};

function printBracketCashers(
  seName: string,
  cashers: { bowler_name: string; total_amount: number; payout_notes: string }[],
  refunds: { bowler_name: string; refund_amount: number }[],
  cost: number
) {
  const sorted = [...cashers].sort((a, b) => _lastName(a.bowler_name).localeCompare(_lastName(b.bowler_name)));
  const sortedRefunds = [...refunds].sort((a, b) => _lastName(a.bowler_name).localeCompare(_lastName(b.bowler_name)));

  const casherRows = sorted.map((c, i) =>
    `<tr><td class="td">${i + 1}</td><td class="td" style="font-weight:600">${_fmtName(c.bowler_name)}</td><td class="td" style="text-align:right;color:#15803d;font-weight:700">$${Number(c.total_amount).toFixed(2)}</td><td class="td n">${c.payout_notes || ''}</td></tr>`
  ).join('');
  const casherTotal = sorted.reduce((s, c) => s + Number(c.total_amount), 0);

  const refundSection = sortedRefunds.length > 0 ? `
    <h3 style="font-size:13px;margin:24px 0 8px;">Refunds Issued at Bracket Generation</h3>
    <table><thead><tr><th>#</th><th>Bowler</th><th style="text-align:right">Refund</th></tr></thead><tbody>
    ${sortedRefunds.map((r, i) => `<tr><td class="td">${i + 1}</td><td class="td" style="font-weight:600">${_fmtName(r.bowler_name)}</td><td class="td" style="text-align:right;color:#b45309;font-weight:700">$${Number(r.refund_amount).toFixed(2)}</td></tr>`).join('')}
    </tbody><tfoot><tr><td colspan="2" style="padding:8px 10px;font-weight:700;text-align:right;border-top:2px solid #111;">Total Refunded</td>
    <td style="padding:8px 10px;font-weight:700;text-align:right;border-top:2px solid #111;color:#b45309;">$${sortedRefunds.reduce((s, r) => s + Number(r.refund_amount), 0).toFixed(2)}</td></tr></tfoot></table>
  ` : '';

  const html = `<!DOCTYPE html><html><head><title>Bracket Cashers – ${seName}</title>
    <style>@page{size:A4 portrait;margin:15mm} body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:0} h2{font-size:15px;margin:0 0 3px} .sub{color:#6b7280;font-size:11px;margin:0 0 16px} table{width:100%;border-collapse:collapse} th{background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;font-size:11px} .td{padding:6px 10px;border-bottom:1px solid #e5e7eb} .n{font-size:10px;color:#6b7280} .footer{color:#9ca3af;font-size:10px;margin-top:20px}</style>
    </head><body>
    <h2>Bracket Cashers – ${seName}</h2>
    <p class="sub">Entry cost: $${Number(cost).toFixed(2)} each</p>
    ${cashers.length > 0
      ? `<table><thead><tr><th>#</th><th>Bowler</th><th style="text-align:right">Winnings</th><th>Notes</th></tr></thead><tbody>${casherRows}</tbody>
         <tfoot><tr><td colspan="2" style="padding:8px 10px;font-weight:700;text-align:right;border-top:2px solid #111;">Total Paid Out</td>
         <td style="padding:8px 10px;font-weight:700;text-align:right;border-top:2px solid #111;">$${casherTotal.toFixed(2)}</td><td></td></tr></tfoot></table>`
      : '<p style="color:#9ca3af;font-style:italic">No cashers yet – scores not yet pulled.</p>'
    }
    ${refundSection}
    <p class="footer">Printed ${new Date().toLocaleString()}</p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=700,height=600');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

/* ─── Print Bracket Entries ──────────────────────────────────────────────────── */
function printBracketEntries(seName: string, entries: BracketEntry[], brackets: BracketData[]) {
  // Build bowler_id → bracket_numbers[] from round 1 of each bracket
  const bracketMap: Record<number, number[]> = {};
  for (const b of brackets) {
    const r1 = b.rounds.find(r => r.round_number === 1);
    if (!r1) continue;
    for (const m of r1.matchups) {
      if (m.entry1_bowler_id) {
        if (!bracketMap[m.entry1_bowler_id]) bracketMap[m.entry1_bowler_id] = [];
        bracketMap[m.entry1_bowler_id].push(b.bracket_number);
      }
      if (m.entry2_bowler_id && !m.is_bye) {
        if (!bracketMap[m.entry2_bowler_id]) bracketMap[m.entry2_bowler_id] = [];
        bracketMap[m.entry2_bowler_id].push(b.bracket_number);
      }
    }
  }

  const sorted = [...entries].sort((a, b) => _lastName(a.name).localeCompare(_lastName(b.name)));
  const rows = sorted.map((e, i) => {
    const bNums = (bracketMap[e.bowler_id] || []).sort((a, b) => a - b).join(', ') || '–';
    return `<tr><td class="td">${i + 1}</td><td class="td bname">${_fmtName(e.name)}</td><td class="td ctr">${e.entryCount}</td><td class="td">${bNums}</td></tr>`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><title>Bracket Entries – ${seName}</title>
    <style>@page{size:A4 portrait;margin:15mm} body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:0} h2{font-size:15px;margin:0 0 3px} .sub{color:#6b7280;font-size:11px;margin:0 0 16px} table{width:100%;border-collapse:collapse} th{background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;font-size:11px} .td{padding:6px 10px;border-bottom:1px solid #e5e7eb} .bname{font-weight:600} .ctr{text-align:center} .footer{color:#9ca3af;font-size:10px;margin-top:20px}</style>
    </head><body>
    <h2>Bracket Entries – ${seName}</h2>
    <p class="sub">${sorted.length} bowler${sorted.length !== 1 ? 's' : ''} · sorted by last name</p>
    <table><thead><tr><th>#</th><th>Bowler</th><th style="text-align:center">Entries</th><th>Bracket #s</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="4" style="padding:8px 10px;font-size:10px;color:#6b7280;border-top:2px solid #d1d5db">${sorted.length} bowlers</td></tr></tfoot>
    </table>
    <p class="footer">Printed ${new Date().toLocaleString()}</p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=700,height=700');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

/* ─── Print Alive Bowlers ─────────────────────────────────────────────────────── */
function printAliveBowlers(seName: string, brackets: BracketData[]) {
  // For each bracket, collect entries from round 1 then find eliminated entries via matchup results
  type AliveEntry = { name: string; bracketNumbers: number[] };
  const aliveMap: Record<number, AliveEntry> = {}; // keyed by bowler_id

  for (const b of brackets) {
    const r1 = b.rounds.find(r => r.round_number === 1);
    if (!r1) continue;

    // All original participants in this bracket (entry_id → {bowler_id, name})
    const participantsByEntry: Record<number, { bowlerId: number; name: string }> = {};
    for (const m of r1.matchups) {
      if (m.entry1_id && m.entry1_bowler_id)
        participantsByEntry[m.entry1_id] = { bowlerId: m.entry1_bowler_id, name: m.entry1_name || '' };
      if (m.entry2_id && m.entry2_bowler_id && !m.is_bye)
        participantsByEntry[m.entry2_id] = { bowlerId: m.entry2_bowler_id, name: m.entry2_name || '' };
    }

    // Eliminated entries: those who lost a resolved matchup
    const eliminatedEntries = new Set<number | null>();
    for (const round of b.rounds) {
      for (const m of round.matchups) {
        if (!m.winner_entry_id) continue;
        if (m.winner_entry_id === m.entry1_id && m.entry2_id) eliminatedEntries.add(m.entry2_id);
        if (m.winner_entry_id === m.entry2_id && m.entry1_id) eliminatedEntries.add(m.entry1_id);
      }
    }

    // Alive = original participants minus eliminated
    for (const [entryIdStr, participant] of Object.entries(participantsByEntry)) {
      if (eliminatedEntries.has(Number(entryIdStr))) continue;
      const { bowlerId, name } = participant;
      if (!aliveMap[bowlerId]) aliveMap[bowlerId] = { name, bracketNumbers: [] };
      aliveMap[bowlerId].bracketNumbers.push(b.bracket_number);
    }
  }

  const aliveList = Object.values(aliveMap)
    .sort((a, b) => _lastName(a.name).localeCompare(_lastName(b.name)));

  const rows = aliveList.map((e, i) => {
    const bNums = [...e.bracketNumbers].sort((a, b) => a - b).join(', ');
    return `<tr><td class="td">${i + 1}</td><td class="td bname">${_fmtName(e.name)}</td><td class="td">${bNums}</td></tr>`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><title>Alive Bowlers – ${seName}</title>
    <style>@page{size:A4 portrait;margin:15mm} body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:0} h2{font-size:15px;margin:0 0 3px} .sub{color:#6b7280;font-size:11px;margin:0 0 16px} table{width:100%;border-collapse:collapse} th{background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;font-size:11px} .td{padding:6px 10px;border-bottom:1px solid #e5e7eb} .bname{font-weight:600} .footer{color:#9ca3af;font-size:10px;margin-top:20px}</style>
    </head><body>
    <h2>Alive Bowlers – ${seName}</h2>
    <p class="sub">${aliveList.length} bowler${aliveList.length !== 1 ? 's' : ''} still alive · sorted by last name</p>
    <table><thead><tr><th>#</th><th>Bowler</th><th>Alive in Brackets</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="3" style="padding:10px;color:#9ca3af;text-align:center">No data yet – pull scores first</td></tr>'}</tbody>
    </table>
    <p class="footer">Printed ${new Date().toLocaleString()}</p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=700,height=700');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

/* ─── Financials Panel ───────────────────────────────────────────────────────── */
const FinancialsPanel: React.FC<{ leagueId: number; squadId: number | null; leagueName: string; squadName?: string; onClose: () => void; }> =
  ({ leagueId, squadId, leagueName, squadName, onClose }) => {
    const { t } = useTranslation();
    const [finData, setFinData] = useState<{ sideEvents: Financial[]; totalPot: number; totalCollected: number } | null>(null);
    const [payoutData, setPayoutData] = useState<{ payouts: Payout[]; totalPayouts: number } | null>(null);
    const [loading, setLoading] = useState(true);
    const [printMenuOpen, setPrintMenuOpen] = useState(false);

    const scopeLabel = squadId ? (squadName || 'Squad') : 'All Squads';

    const loadData = () => {
      const finUrl = squadId
        ? `${API}/bookie-league-management/squads/${squadId}/financials`
        : `${API}/bookie-league-management/leagues/${leagueId}/financials`;
      const payUrl = squadId
        ? `${API}/bookie-league-management/squads/${squadId}/payouts`
        : `${API}/bookie-league-management/leagues/${leagueId}/payouts`;
      Promise.all([
        axios.get(finUrl, { headers: authHeader() }),
        axios.get(payUrl, { headers: authHeader() }),
      ])
        .then(([finRes, payRes]) => {
          setFinData(finRes.data);
          setPayoutData(payRes.data);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    };

    useEffect(() => { loadData(); }, [leagueId, squadId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCheckmark = async (payoutId: number, current: number) => {
      const newVal = current ? 0 : 1;
      setPayoutData(prev => prev ? {
        ...prev,
        payouts: prev.payouts.map(p => p.id === payoutId ? { ...p, checkmark: newVal } : p),
      } : prev);
      try {
        await axios.put(`${API}/bookie-league-management/payouts/${payoutId}/checkmark`, { checkmark: newVal }, { headers: authHeader() });
      } catch {
        // revert on error
        setPayoutData(prev => prev ? {
          ...prev,
          payouts: prev.payouts.map(p => p.id === payoutId ? { ...p, checkmark: current } : p),
        } : prev);
      }
    };

    // Group payouts by recipient: bracket rows first, then others, sorted by last name
    const groupedPayouts = (() => {
      if (!payoutData) return [];
      const map = new Map<string, Payout[]>();
      for (const p of payoutData.payouts) {
        const key = `${p.recipient_id}|${p.recipient_name || ''}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(p);
      }
      return Array.from(map.values()).map((rows: Payout[]) => ({
        name: rows[0].recipient_name || `#${rows[0].recipient_id}`,
        rows: [...rows.filter((r: Payout) => r.se_type === 'bracket'), ...rows.filter((r: Payout) => r.se_type !== 'bracket')],
        total: rows.reduce((s: number, r: Payout) => s + Number(r.amount), 0),
      })).sort((a, b) => _lastName(a.name).localeCompare(_lastName(b.name)));
    })();

    const collected = finData ? Number(finData.totalCollected) : 0;
    const payoutsTotal = payoutData ? Number(payoutData.totalPayouts) : 0;
    const balance = collected - payoutsTotal;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
            <div>
              <h2 className="font-bold text-white text-lg">
                {squadId ? t('bookie.financials.titleSquad', 'Squad Financials') : t('bookie.financials.titleAll', 'League Financials')}
              </h2>
              <p className="text-xs text-gray-400">{squadId ? squadName : leagueName} · {scopeLabel}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* ── Print dropdown ── */}
              {(finData || payoutData) && (
                <div className="relative">
                  <button
                    onClick={() => setPrintMenuOpen(o => !o)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg flex items-center gap-1.5"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {printMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-xl z-10 min-w-[180px] overflow-hidden">
                      {finData && (
                        <button
                          onClick={() => { printPayments(leagueName, scopeLabel, finData.sideEvents); setPrintMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-600 flex items-center gap-2"
                        >
                          <span className="text-green-400">💰</span> Print Payments
                        </button>
                      )}
                      {payoutData && (
                        <button
                          onClick={() => { printPayouts(leagueName, scopeLabel, payoutData.payouts); setPrintMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-600 flex items-center gap-2"
                        >
                          <span className="text-red-400">💸</span> Print Payouts (Cashers)
                        </button>
                      )}
                      {finData && payoutData && (
                        <button
                          onClick={() => {
                            printPayments(leagueName, scopeLabel, finData.sideEvents);
                            setTimeout(() => printPayouts(leagueName, scopeLabel, payoutData.payouts), 600);
                            setPrintMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-600 border-t border-gray-600 flex items-center gap-2"
                        >
                          <span className="text-blue-400">🖨️</span> Print Both
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {loading && (
              <div className="flex justify-center py-16">
                <div className="w-7 h-7 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {!loading && (
              <>
                {/* ── Summary Cards ── */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-400">${collected.toFixed(2)}</div>
                    <div className="text-xs text-gray-400 mt-1">💰 Total Collected</div>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-400">${payoutsTotal.toFixed(2)}</div>
                    <div className="text-xs text-gray-400 mt-1">💸 Total Paid Out</div>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                    <div className={`text-2xl font-bold ${balance >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                      {balance < 0 ? '-' : ''}${Math.abs(balance).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">📊 Admin Balance</div>
                  </div>
                </div>

                {/* ── Payments Table ── */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
                    <span className="text-green-400">💰</span> Payments by Bowlers
                  </h3>
                  {!finData || finData.sideEvents.length === 0 ? (
                    <p className="text-center text-gray-500 py-6 text-sm">No side event payments yet</p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-700">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-700/60 text-gray-400 text-xs uppercase">
                            <th className="px-4 py-2.5 text-left">Side Event</th>
                            <th className="px-4 py-2.5 text-left">Type</th>
                            {!squadId && <th className="px-4 py-2.5 text-left">Squad</th>}
                            <th className="px-4 py-2.5 text-center">Entries</th>
                            <th className="px-4 py-2.5 text-right">Cost</th>
                            <th className="px-4 py-2.5 text-right">Total Pot</th>
                          </tr>
                        </thead>
                        <tbody>
                          {finData.sideEvents.map((f, i) => (
                            <tr key={i} className="border-t border-gray-700/50 hover:bg-gray-700/20">
                              <td className="px-4 py-2.5 text-white font-medium">{f.name}</td>
                              <td className="px-4 py-2.5 text-gray-400">{TYPE_LABELS[f.type] || f.type}</td>
                              {!squadId && (
                                <td className="px-4 py-2.5 text-gray-400">
                                  {f.squad_name} {f.squad_date ? `(${fmtDate(f.squad_date)})` : ''}
                                </td>
                              )}
                              <td className="px-4 py-2.5 text-center text-gray-300">{f.total_entries}</td>
                              <td className="px-4 py-2.5 text-right text-gray-300">${Number(f.cost).toFixed(2)}</td>
                              <td className="px-4 py-2.5 text-right font-bold text-green-400">${Number(f.total_pot).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-600 bg-gray-700/40">
                            <td colSpan={!squadId ? 5 : 4} className="px-4 py-2.5 text-right text-sm font-semibold text-gray-300">
                              Total Collected
                            </td>
                            <td className="px-4 py-2.5 text-right font-bold text-green-400">${collected.toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </section>

                {/* ── Payouts Table ── */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
                    <span className="text-red-400">💸</span> Payouts to Bowlers (Cashers)
                  </h3>
                  {!payoutData || payoutData.payouts.length === 0 ? (
                    <p className="text-center text-gray-500 py-6 text-sm">No payouts recorded yet</p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-gray-700">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-700/60 text-gray-400 text-xs uppercase">
                            <th className="px-4 py-2.5 text-left">Recipient</th>
                            <th className="px-4 py-2.5 text-left">Side Event</th>
                            <th className="px-4 py-2.5 text-left">Type</th>
                            {!squadId && <th className="px-4 py-2.5 text-left">Squad</th>}
                            <th className="px-4 py-2.5 text-right">Amount</th>
                            <th className="px-4 py-2.5 text-left">Date</th>
                            <th className="px-4 py-2.5 text-left">Notes</th>
                            <th className="px-4 py-2.5 text-center">✓</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedPayouts.map((g, gi) => {
                            const multi = g.rows.length > 1;
                            const rowBg = multi ? 'bg-indigo-950/20' : '';
                            const groupBorderTop = gi > 0 ? 'border-t-2 border-gray-500' : '';
                            return (
                              <React.Fragment key={`group-${g.rows[0].id}`}>
                                {g.rows.map((p, ri) => (
                                  <tr key={p.id} className={`hover:bg-gray-700/25 ${rowBg} ${ri === 0 ? groupBorderTop : 'border-t border-gray-700/40'}`}>
                                    <td className="px-4 py-2.5 text-white font-medium">{ri === 0 ? _fmtName(g.name) : <span className="text-gray-600 pl-3 text-xs">└─</span>}</td>
                                    <td className="px-4 py-2.5 text-gray-300">{p.se_name}</td>
                                    <td className="px-4 py-2.5 text-gray-400">{TYPE_LABELS[p.se_type] || p.se_type}</td>
                                    {!squadId && (
                                      <td className="px-4 py-2.5 text-gray-400">
                                        {p.squad_name} {p.squad_date ? `(${fmtDate(p.squad_date)})` : ''}
                                      </td>
                                    )}
                                    <td className="px-4 py-2.5 text-right font-bold text-red-400">${Number(p.amount).toFixed(2)}</td>
                                    <td className="px-4 py-2.5 text-gray-400 text-xs">
                                      {p.payout_date ? new Date(p.payout_date).toLocaleDateString() : '—'}
                                    </td>
                                    <td className="px-4 py-2.5 text-gray-500 text-xs">{p.notes || '—'}</td>
                                    <td className="px-4 py-2.5 text-center">
                                      <input
                                        type="checkbox"
                                        checked={!!p.checkmark}
                                        onChange={() => handleCheckmark(p.id, p.checkmark)}
                                        className="w-4 h-4 accent-green-500 cursor-pointer"
                                        title="Mark as paid out"
                                      />
                                    </td>
                                  </tr>
                                ))}
                                {multi && (
                                  <tr className="border-t border-emerald-700/50 bg-emerald-950/30">
                                    <td colSpan={!squadId ? 7 : 6} className="px-4 py-1.5 text-right text-xs font-semibold text-emerald-300">
                                      Subtotal — {_fmtName(g.name)}
                                    </td>
                                    <td className="px-4 py-1.5 text-right font-bold text-emerald-400 text-xs">${g.total.toFixed(2)}</td>
                                    <td />
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-600 bg-gray-700/40">
                            <td colSpan={!squadId ? 7 : 6} className="px-4 py-2.5 text-right text-sm font-semibold text-gray-300">
                              Total Paid Out
                            </td>
                            <td className="px-4 py-2.5 text-right font-bold text-red-400">${payoutsTotal.toFixed(2)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

/* ─── Entries Panel (non-bracket) ───────────────────────────────────────── */
const EntriesPanel: React.FC<{ sideEvent: SideEvent; leagueId: number; onRefresh: () => void; }> = ({ sideEvent, leagueId, onRefresh }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Bowler[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [addingBowler, setAddingBowler] = useState<number | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [groupSearch, setGroupSearch] = useState('');
  const [groupResults, setGroupResults] = useState<Bowler[]>([]);
  const groupSearchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [showEntries, setShowEntries] = useState(false);
  const [entrySearch, setEntrySearch] = useState('');

  const loadEntries = useCallback(() => {
    setLoadingEntries(true);
    axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`, { headers: authHeader() })
      .then(r => setEntries(r.data))
      .catch(() => {})
      .finally(() => setLoadingEntries(false));
  }, [sideEvent.id]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    if (!search.trim()) { setResults([]); return; }
    searchTimeout.current = setTimeout(() => {
      const eqs: string[] = [`query=${encodeURIComponent(search)}`];
      if (sideEvent.gender === 'F') eqs.push('gender=F');
      if (sideEvent.min_age) eqs.push(`min_age=${sideEvent.min_age}`);
      if (sideEvent.max_age) eqs.push(`max_age=${sideEvent.max_age}`);
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowlers/search?${eqs.join('&')}`, { headers: authHeader() })
        .then(r => setResults(r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [search, leagueId]);

  useEffect(() => {
    clearTimeout(groupSearchTimeout.current);
    if (!groupSearch.trim()) { setGroupResults([]); return; }
    groupSearchTimeout.current = setTimeout(() => {
      const eqs: string[] = [`query=${encodeURIComponent(groupSearch)}`];
      if (sideEvent.gender === 'F') eqs.push('gender=F');
      if (sideEvent.min_age) eqs.push(`min_age=${sideEvent.min_age}`);
      if (sideEvent.max_age) eqs.push(`max_age=${sideEvent.max_age}`);
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowler-group/search?${eqs.join('&')}`, { headers: authHeader() })
        .then(r => setGroupResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(groupSearchTimeout.current);
  }, [groupSearch, leagueId]);

  const addEntry = async (bowler: Bowler) => {
    setAddingBowler(bowler.id);
    try {
      await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`,
        { bowlerId: bowler.id, paid: true }, { headers: authHeader() });
      setSearch(''); setResults([]); setGroupSearch(''); setGroupResults([]);
      loadEntries(); onRefresh();
    } catch { } finally { setAddingBowler(null); }
  };

  const removeEntry = async (entry: Entry) => {
    setRemoving(entry.bowler_id);
    try {
      await axios.delete(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`,
        { data: { bowlerId: entry.bowler_id }, headers: authHeader() });
      loadEntries(); onRefresh();
    } catch { } finally { setRemoving(null); }
  };

  const totalPot = entries.length * Number(sideEvent.cost);
  const sortedEntries = [...entries].sort((a, b) => _lastName(a.bowler_name).localeCompare(_lastName(b.bowler_name)));
  const filteredEntries = entrySearch.trim()
    ? sortedEntries.filter(e => e.bowler_name.toLowerCase().includes(entrySearch.toLowerCase()))
    : sortedEntries;

  return (
    <div>
      {/* Search & Add — OUTSIDE collapsible, always visible */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        {/* To add an entry — my bowlers */}
        <div>
          <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">To add an entry — Search from my bowlers</label>
          <div className="relative">
            <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)}
              placeholder="Type name, id or email..."
              className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
            {groupSearch && (
              <button onClick={() => { setGroupSearch(''); setGroupResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {groupResults.length > 0 && (
              <div className="absolute z-20 top-full mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                {groupResults.map(b => (
                  <button key={b.id} onClick={() => addEntry(b)} disabled={addingBowler === b.id}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-600 transition-colors text-left border-b border-gray-600 last:border-0">
                    <div>
                      <div className="text-white text-sm font-medium">{b.name}</div>
                      <div className="text-gray-400 text-xs">{b.email}</div>
                    </div>
                    {Number(sideEvent.cost) > 0 && <span className="text-green-400 text-xs font-semibold">${Number(sideEvent.cost).toFixed(2)}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* To add an entry — all bowlers */}
        <div>
          <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">To add an entry — Search from all bowlers</label>
          <div className="relative">
            <input
              value={search} onChange={e => setSearch(e.target.value)} placeholder={t('bookie.entries.searchBowler', 'Search bowler by name...')}
              className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
            {search && (
              <button onClick={() => { setSearch(''); setResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {results.length > 0 && (
              <div className="absolute z-20 top-full mt-1 w-full bg-gray-750 border border-gray-600 rounded-lg shadow-xl overflow-hidden" style={{ backgroundColor: '#2d3748' }}>
                {results.map(b => (
                  <button key={b.id} onClick={() => addEntry(b)}
                    disabled={addingBowler === b.id}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-600 transition-colors text-left border-b border-gray-600 last:border-0">
                    <div>
                      <div className="text-white text-sm font-medium">{b.name}</div>
                      <div className="text-gray-400 text-xs">{b.email}</div>
                    </div>
                    <div className="text-right ml-2">
                      {Number(sideEvent.cost) > 0 && <div className="text-green-400 text-xs font-semibold">${Number(sideEvent.cost).toFixed(2)}</div>}
                      <div className="text-xs text-purple-300">+ {t('bookie.entries.collect', 'Collect')}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enrolled bowlers — collapsible, default collapsed */}
      <div className="mb-4 border border-gray-700 rounded-xl overflow-hidden">
        <button onClick={() => setShowEntries(s => !s)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-700/40 hover:bg-gray-700/60 text-sm text-gray-300 transition-colors">
          <div className="flex items-center gap-3">
            <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${showEntries ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-medium">{t('bookie.entries.enrolled', 'Enrolled Bowlers')} ({entries.length})</span>
            {totalPot > 0 && <span className="text-green-400 font-semibold text-xs">${totalPot.toFixed(2)} pot</span>}
          </div>
        </button>
        {showEntries && (
          <div className="p-3 space-y-3">
            {/* Find enrolled bowler search */}
            <div>
              <label className="block text-sm font-bold text-white mb-2">Find Enrolled Bowler</label>
              <input value={entrySearch} onChange={e => setEntrySearch(e.target.value)}
                placeholder="Search enrolled bowlers..."
                className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
            </div>
            {/* Entries list */}
            {loadingEntries && <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>}
            {!loadingEntries && entries.length === 0 && (
              <div className="text-center text-gray-500 py-8 text-sm">{t('bookie.entries.noEntries', 'No entries yet. Search to add bowlers.')}</div>
            )}
            {!loadingEntries && filteredEntries.length > 0 && (
              <div className="space-y-1.5">
                {filteredEntries.map((entry, i) => (
                  <div key={entry.id || i} className="flex items-center justify-between bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${entry.paid ? 'bg-green-400' : 'bg-yellow-400'}`} />
                      <div>
                        <div className="text-white text-sm font-medium">{_fmtName(entry.bowler_name)}</div>
                        {entry.paid
                          ? <div className="text-xs text-green-400">{t('bookie.entries.paid', 'Paid')} · ${Number(sideEvent.cost).toFixed(2)}</div>
                          : <div className="text-xs text-yellow-400">{t('bookie.entries.unpaid', 'Unpaid')}</div>}
                      </div>
                    </div>
                    <button onClick={() => removeEntry(entry)} disabled={removing === entry.bowler_id}
                      className="text-gray-400 hover:text-red-400 transition-colors p-1 disabled:opacity-50">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {!loadingEntries && entrySearch.trim() && filteredEntries.length === 0 && (
              <p className="text-center text-gray-500 py-4 text-sm">No enrolled bowlers match your search.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Doubles Panel ──────────────────────────────────────────────────────── */
const DoublesPanel: React.FC<{ sideEvent: SideEvent; leagueId: number; onRefresh: () => void }> = ({ sideEvent, leagueId, onRefresh }) => {
  const { t } = useTranslation();

  // Entry state
  const [entries, setEntries] = useState<Entry[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Bowler[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [addingBowler, setAddingBowler] = useState<number | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [groupSearch, setGroupSearch] = useState('');
  const [groupResults, setGroupResults] = useState<Bowler[]>([]);
  const groupSearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Doubles state
  const [doubles, setDoubles] = useState<{ double_id: number; bowlers: any[] }[]>([]);
  const [doublesGenerated, setDoublesGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState('');
  const [generateError, setGenerateError] = useState('');

  // Scores state
  const [scores, setScores] = useState<{ double_id: number; total_score: number; bowlers: any[] }[]>([]);
  const [useHandicap, setUseHandicap] = useState(!!sideEvent.hdcp);
  const [loadingScores, setLoadingScores] = useState(false);

  // Swap state
  const [selectedParticipant, setSelectedParticipant] = useState<{ participant_id: number; double_id: number; bowler_name: string } | null>(null);
  const [swapping, setSwapping] = useState(false);

  // UI
  const [expandedDoubles, setExpandedDoubles] = useState<Set<number>>(new Set());
  const [showDoubles, setShowDoubles] = useState(false);
  const [showEntries, setShowEntries] = useState(true);

  const loadEntries = useCallback(() => {
    setLoadingEntries(true);
    axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`, { headers: authHeader() })
      .then(r => setEntries(r.data))
      .catch(() => {})
      .finally(() => setLoadingEntries(false));
  }, [sideEvent.id]);

  const loadDoubles = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/doubles`, { headers: authHeader() });
      const d: { double_id: number; bowlers: any[] }[] = r.data.doubles || [];
      setDoubles(d);
      setDoublesGenerated(d.length > 0);
    } catch {}
  }, [sideEvent.id]);

  const loadScores = useCallback(async (hdcp?: boolean) => {
    const hFlag = hdcp !== undefined ? hdcp : useHandicap;
    setLoadingScores(true);
    try {
      const r = await axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/doubles-scores?handicap=${hFlag}`, { headers: authHeader() });
      setScores(r.data.scores || []);
    } catch {} finally { setLoadingScores(false); }
  }, [sideEvent.id, useHandicap]);

  useEffect(() => { loadEntries(); loadDoubles(); }, [loadEntries, loadDoubles]);
  useEffect(() => { if (doublesGenerated) loadScores(); }, [doublesGenerated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    if (!search.trim()) { setResults([]); return; }
    searchTimeout.current = setTimeout(() => {
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowlers/search?query=${encodeURIComponent(search)}`, { headers: authHeader() })
        .then(r => setResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [search, leagueId]);

  useEffect(() => {
    clearTimeout(groupSearchTimeout.current);
    if (!groupSearch.trim()) { setGroupResults([]); return; }
    groupSearchTimeout.current = setTimeout(() => {
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowler-group/search?query=${encodeURIComponent(groupSearch)}`, { headers: authHeader() })
        .then(r => setGroupResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(groupSearchTimeout.current);
  }, [groupSearch, leagueId]);

  const addEntry = async (bowler: Bowler) => {
    setAddingBowler(bowler.id);
    try {
      await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`,
        { bowlerId: bowler.id, paid: true }, { headers: authHeader() });
      setSearch(''); setResults([]); setGroupSearch(''); setGroupResults([]);
      loadEntries(); onRefresh();
    } catch {} finally { setAddingBowler(null); }
  };

  const removeEntry = async (entry: Entry) => {
    setRemoving(entry.bowler_id);
    try {
      await axios.delete(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`,
        { data: { bowlerId: entry.bowler_id }, headers: authHeader() });
      loadEntries(); onRefresh();
    } catch {} finally { setRemoving(null); }
  };

  const handleGenerateDoubles = async () => {
    setGenerating(true); setGenerateMsg(''); setGenerateError('');
    try {
      const res = await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/generate-doubles`, {}, { headers: authHeader() });
      setGenerateMsg(res.data?.message || 'Doubles generated!');
      await loadEntries();
      await loadDoubles();
      await loadScores(useHandicap);
      onRefresh();
    } catch (e: any) {
      setGenerateError(e?.response?.data?.error || 'Failed to generate doubles');
    } finally { setGenerating(false); }
  };

  const handleParticipantClick = (p: { participant_id: number; double_id: number; bowler_name: string }) => {
    if (!selectedParticipant) {
      setSelectedParticipant(p);
    } else if (selectedParticipant.participant_id === p.participant_id) {
      setSelectedParticipant(null);
    } else if (selectedParticipant.double_id === p.double_id) {
      setSelectedParticipant(p); // re-select within same double
    } else {
      handleSwap(selectedParticipant.participant_id, p.participant_id);
    }
  };

  const handleSwap = async (pid1: number, pid2: number) => {
    setSwapping(true);
    try {
      await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/swap-doubles`,
        { participantId1: pid1, participantId2: pid2 }, { headers: authHeader() });
      setSelectedParticipant(null);
      await loadDoubles();
      await loadScores(useHandicap);
    } catch {} finally { setSwapping(false); }
  };

  const toggleHandicap = () => {
    const next = !useHandicap;
    setUseHandicap(next);
    if (doublesGenerated) loadScores(next);
    axios.patch(`${API}/bookie-league-management/side-events/${sideEvent.id}/hdcp`,
      { hdcp: next }, { headers: authHeader() }).catch(() => {});
  };

  const toggleDouble = (id: number) => setExpandedDoubles(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const getScoreForDouble = (id: number) => scores.find(s => s.double_id === id);
  const totalPot = entries.length * Number(sideEvent.cost);

  return (
    <div>
      {/* Header toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{entries.length} enrolled</span>
          {totalPot > 0 && <span className="text-green-400 font-semibold text-sm">${totalPot.toFixed(2)} pot</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleGenerateDoubles} disabled={generating || (entries.length === 0 && !doublesGenerated)}
            className="text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors">
            {generating && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {doublesGenerated ? 'Regenerate Doubles' : 'Generate Doubles'}
          </button>
          {doublesGenerated && (
            <>
              <button onClick={() => loadScores(useHandicap)} disabled={loadingScores}
                className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-colors">
                {loadingScores && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Refresh Scores
              </button>
              <button onClick={toggleHandicap}
                className={`text-xs px-3 py-1.5 rounded-lg text-white font-semibold transition-colors ${useHandicap ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-600 hover:bg-gray-500'}`}>
                Hdcp {useHandicap ? 'ON' : 'OFF'}
              </button>
            </>
          )}
        </div>
      </div>

      {generateMsg && <div className="mb-3 text-xs px-3 py-2 bg-green-900/30 border border-green-600/40 text-green-300 rounded-lg">{generateMsg}</div>}
      {generateError && <div className="mb-3 text-xs px-3 py-2 bg-red-900/30 border border-red-600/40 text-red-300 rounded-lg">{generateError}</div>}

      {selectedParticipant && (
        <div className="mb-3 text-xs px-3 py-2 bg-yellow-900/30 border border-yellow-600/40 text-yellow-200 rounded-lg flex items-center justify-between">
          <span>Selected: <strong>{selectedParticipant.bowler_name}</strong> — click a bowler from a different double to swap</span>
          <button onClick={() => setSelectedParticipant(null)} className="text-yellow-400 hover:text-white ml-2">✕</button>
        </div>
      )}

      {/* Entries section (collapsible) */}
      <div className="mb-4 border border-gray-700 rounded-xl overflow-hidden">
        <button onClick={() => setShowEntries(s => !s)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-700/40 hover:bg-gray-700/60 text-sm text-gray-300 transition-colors">
          <span className="font-medium">{t('bookie.entries.enrolled', 'Entries')} ({entries.length})</span>
          <svg className={`w-4 h-4 transition-transform ${showEntries ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showEntries && (
          <div className="p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {/* Search from my bowlers */}
              <div>
                <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Search from my bowlers</label>
                <div className="relative">
                  <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Type name, id or email..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
                  {groupSearch && (
                    <button onClick={() => { setGroupSearch(''); setGroupResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                  {groupResults.length > 0 && (
                    <div className="absolute z-20 top-full mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                      {groupResults.map(b => (
                        <button key={b.id} onClick={() => addEntry(b)} disabled={addingBowler === b.id}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-700 border-b border-gray-700 last:border-0 text-left transition-colors">
                          <div>
                            <div className="text-white text-sm font-medium">{b.name}</div>
                            <div className="text-gray-400 text-xs">{b.email}</div>
                          </div>
                          {Number(sideEvent.cost) > 0 && <span className="text-green-400 text-xs ml-2 font-semibold">${Number(sideEvent.cost).toFixed(2)}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Search from all bowlers */}
              <div>
                <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Search from all bowlers</label>
                <div className="relative">
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('bookie.entries.searchBowler', 'Search bowler by name...')}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
                  {search && (
                    <button onClick={() => { setSearch(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                  {results.length > 0 && (
                    <div className="absolute z-20 top-full mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-xl overflow-hidden">
                      {results.map(b => (
                        <button key={b.id} onClick={() => addEntry(b)} disabled={addingBowler === b.id}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-700 border-b border-gray-700 last:border-0 text-left transition-colors">
                          <div>
                            <div className="text-white text-sm font-medium">{b.name}</div>
                            <div className="text-gray-400 text-xs">{b.email}</div>
                          </div>
                          {Number(sideEvent.cost) > 0 && <span className="text-green-400 text-xs ml-2 font-semibold">${Number(sideEvent.cost).toFixed(2)}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {loadingEntries && <div className="flex justify-center py-4"><div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>}
            {!loadingEntries && entries.length === 0 && <div className="text-center text-gray-500 py-4 text-sm">{t('bookie.entries.noEntries', 'No entries yet.')}</div>}
            {!loadingEntries && entries.length > 0 && (
              <div className="space-y-1">
                {entries.map((entry, i) => (
                  <div key={entry.id || i} className="flex items-center justify-between bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${entry.paid ? 'bg-green-400' : 'bg-yellow-400'}`} />
                      <span className="text-white text-sm font-medium">{entry.bowler_name}</span>
                      {entry.paid
                        ? <span className="text-xs text-green-400">{t('bookie.entries.paid', 'Paid')} · ${Number(sideEvent.cost).toFixed(2)}</span>
                        : <span className="text-xs text-yellow-400">{t('bookie.entries.unpaid', 'Unpaid')}</span>}
                    </div>
                    <button onClick={() => removeEntry(entry)} disabled={removing === entry.bowler_id}
                      className="text-gray-400 hover:text-red-400 transition-colors p-1 disabled:opacity-50">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Doubles list */}
      {doublesGenerated && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setShowDoubles(s => !s)}
              className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity">
              <h4 className="text-sm font-semibold text-gray-300">Doubles ({doubles.length} pairs)</h4>
              <svg className={`w-4 h-4 text-gray-500 transition-transform ${showDoubles ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {swapping && <div className="text-xs text-gray-400 flex items-center gap-1.5"><div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />Swapping...</div>}
          </div>
          {showDoubles && <div className="space-y-2">
            {doubles.map((dbl, rank) => {
              const scoreEntry = getScoreForDouble(dbl.double_id);
              const isExpanded = expandedDoubles.has(dbl.double_id);
              const realBowlers = dbl.bowlers.filter((b: any) => b.bowler_id !== 1);
              const hasBlind = dbl.bowlers.some((b: any) => b.bowler_id === 1);
              return (
                <div key={dbl.double_id} className="border border-gray-600/50 rounded-xl overflow-hidden">
                  <button onClick={() => toggleDouble(dbl.double_id)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-700/40 hover:bg-gray-700/60 transition-colors text-left">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 font-mono w-6 text-right">{rank + 1}.</span>
                      <span className="text-sm font-medium text-white">
                        {realBowlers.map((b: any) => b.bowler_name).join(' & ')}
                        {hasBlind && <span className="text-gray-500 italic"> & Blind</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {scoreEntry && scoreEntry.total_score > 0 && (
                        <span className="text-sm font-mono font-semibold text-green-400">{scoreEntry.total_score}</span>
                      )}
                      <svg className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 py-3 bg-gray-800/40 space-y-2">
                      {dbl.bowlers.map((bowler: any) => {
                        const bScore = scoreEntry?.bowlers.find((bs: any) => bs.participant_id === bowler.participant_id);
                        const isSelected = selectedParticipant?.participant_id === bowler.participant_id;
                        const isBlind = bowler.bowler_id === 1;
                        return (
                          <button key={bowler.participant_id}
                            onClick={() => !isBlind && handleParticipantClick({ participant_id: bowler.participant_id, double_id: dbl.double_id, bowler_name: bowler.bowler_name })}
                            disabled={swapping || isBlind}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors text-left
                              ${isSelected
                                ? 'bg-yellow-900/30 border-yellow-500 text-yellow-200'
                                : isBlind
                                  ? 'bg-gray-700/20 border-gray-700/40 text-gray-500 cursor-default'
                                  : selectedParticipant && selectedParticipant.double_id !== dbl.double_id
                                    ? 'bg-blue-900/20 border-blue-600/40 text-white hover:bg-blue-800/30 cursor-pointer'
                                    : 'bg-gray-700/30 border-gray-600/50 text-white hover:bg-gray-600/30 cursor-pointer'}`}>
                            <div className="flex items-center gap-2">
                              {isSelected
                                ? <span className="text-yellow-400 text-xs">●</span>
                                : !isBlind && <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>}
                              <span className="text-sm font-medium">{isBlind ? 'Blind (200/game)' : bowler.bowler_name}</span>
                            </div>
                            {bScore && (
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-mono text-gray-300">{bScore.score}</span>
                                {useHandicap && bScore.handicap > 0 && <span className="text-teal-400">+{bScore.handicap}</span>}
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {scoreEntry && scoreEntry.total_score > 0 && (
                        <div className="flex justify-end pt-1 border-t border-gray-700/50">
                          <span className="text-xs text-gray-400">
                            Total: <span className="font-mono text-white font-semibold">{scoreEntry.total_score}</span>
                            {useHandicap && <span className="text-teal-300 ml-1">(w/ hdcp)</span>}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
        </div>
      )}
    </div>
  );
};

/* ─── Bracket Tree Visual (sub-component) ──────────────────────────────── */
const BracketTree: React.FC<{ bracket: BracketData; totalRounds: number }> = ({ bracket, totalRounds }) => (
  <div className="overflow-x-auto pb-2">
    <div className="flex gap-2" style={{ minWidth: `${totalRounds * 188}px` }}>
      {Array.from({ length: totalRounds }, (_, ri) => {
        const roundNum = ri + 1;
        const round = bracket.rounds.find(r => r.round_number === roundNum);
        const slotCount = Math.pow(2, totalRounds - roundNum);
        const slotHeightPx = Math.pow(2, roundNum - 1) * 58;
        const header = roundNum === totalRounds ? 'Final' : `Round ${roundNum}${round ? ` · G${round.game_number}` : ''}`;
        return (
          <div key={roundNum} className="flex flex-col" style={{ flex: '0 0 180px' }}>
            <div className="text-[10px] text-gray-500 text-center pb-1 border-b border-gray-700 mb-1">{header}</div>
            <div className="flex flex-col flex-1">
              {Array.from({ length: slotCount }, (_, mi) => {
                const m = round?.matchups?.[mi];
                const e1Won = m && m.winner_entry_id === m.entry1_id;
                const e2Won = m && m.winner_entry_id === m.entry2_id;
                return (
                  <div key={mi} className="flex items-center justify-center" style={{ height: `${slotHeightPx}px` }}>
                    <div className={`w-full border rounded overflow-hidden text-[11px] ${m ? 'border-gray-600' : 'border-gray-800 opacity-40'}`}>
                      <div className={`flex items-center justify-between px-2 py-1 border-b border-gray-700 ${e1Won ? 'bg-green-900/40 text-green-300 font-semibold' : m?.winner_entry_id ? 'text-gray-500' : 'text-gray-300'}`}>
                        <span className="truncate max-w-[110px]">{m?.entry1_name ?? <span className="text-gray-600 italic text-[10px]">TBD</span>}</span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          {m?.entry1_score != null && <span className="font-mono text-[10px]">{m.entry1_score}</span>}
                          {e1Won && <span className="text-green-400 text-[10px]">✓</span>}
                        </div>
                      </div>
                      <div className={`flex items-center justify-between px-2 py-1 ${e2Won ? 'bg-green-900/40 text-green-300 font-semibold' : m?.winner_entry_id ? 'text-gray-500' : 'text-gray-300'}`}>
                        <span className="truncate max-w-[110px]">
                          {m?.is_bye ? <span className="text-gray-600 italic text-[10px]">BYE</span> : (m?.entry2_name ?? <span className="text-gray-600 italic text-[10px]">TBD</span>)}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          {!m?.is_bye && m?.entry2_score != null && <span className="font-mono text-[10px]">{m.entry2_score}</span>}
                          {e2Won && <span className="text-green-400 text-[10px]">✓</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ─── Bracket Entries Panel ─────────────────────────────────────────────── */
const BracketEntriesPanel: React.FC<{ sideEvent: SideEvent; leagueId: number; onRefresh: () => void; }> = ({ sideEvent, leagueId, onRefresh }) => {
  // Entry state
  const [entries, setEntries] = useState<BracketEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(false);

  // Search + add-entry flow
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Bowler[]>([]);
  const [selectedBowler, setSelectedBowler] = useState<Bowler | null>(null);
  const [qty, setQty] = useState(1);
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingBowler, setRemovingBowler] = useState<number | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [groupSearch, setGroupSearch] = useState('');
  const [groupResults, setGroupResults] = useState<Bowler[]>([]);
  const groupSearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Bracket state
  const [bracketsExist, setBracketsExist] = useState(false);
  const [bracketsDisplay, setBracketsDisplay] = useState<BracketData[]>([]);
  const [totalRounds, setTotalRounds] = useState(3);
  const [bracketCost, setBracketCost] = useState(0);
  const [loadingBrackets, setLoadingBrackets] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [generateMsg, setGenerateMsg] = useState('');
  const [deletingBrackets, setDeletingBrackets] = useState(false);
  const [bracketPublished, setBracketPublished] = useState(0);
  const [togglingPublish, setTogglingPublish] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullMsg, setPullMsg] = useState('');
  const [pullingHdcp, setPullingHdcp] = useState(false);
  const [expandedBrackets, setExpandedBrackets] = useState<Set<number>>(new Set([1]));
  const [cashers, setCashers] = useState<{ bowler_name: string; total_amount: number; payout_notes: string }[]>([]);
  const [entrySearch, setEntrySearch] = useState('');
  const [refunds, setRefunds] = useState<{ bowler_name: string; refund_amount: number }[]>([]);
  const [showEntries, setShowEntries] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/bracket-entries`, { headers: authHeader() });
      setEntries(res.data.bowlerEntries || []);
      setTotalEntries(res.data.totalEntries || 0);
    } catch { } finally { setLoading(false); }
  }, [sideEvent.id]);

  const loadBrackets = useCallback(async () => {
    setLoadingBrackets(true);
    try {
      const [dispRes, cashRes] = await Promise.all([
        axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/brackets-display`, { headers: authHeader() }),
        axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/bracket-cashers`, { headers: authHeader() }),
      ]);
      const bd: BracketData[] = dispRes.data.brackets || [];
      setBracketsDisplay(bd);
      setBracketsExist(bd.length > 0);
      setTotalRounds(dispRes.data.totalRounds ?? 3);
      setBracketCost(dispRes.data.cost ?? 0);
      setBracketPublished(dispRes.data.published ?? 0);
      setCashers(cashRes.data.cashers || []);
      setRefunds(cashRes.data.refunds || []);
    } catch { setBracketsExist(false); } finally { setLoadingBrackets(false); }
  }, [sideEvent.id]);

  useEffect(() => { loadData(); loadBrackets(); }, [loadData, loadBrackets]);

  // Search
  useEffect(() => {
    clearTimeout(searchTimeout.current);
    if (!search.trim()) { setSearchResults([]); return; }
    // Don't re-run the search if a bowler was just selected (search was set to their name)
    if (selectedBowler && search === selectedBowler.name) { return; }
    searchTimeout.current = setTimeout(() => {
      const eqs: string[] = [`query=${encodeURIComponent(search)}`];
      if (sideEvent.gender === 'F') eqs.push('gender=F');
      if (sideEvent.min_age) eqs.push(`min_age=${sideEvent.min_age}`);
      if (sideEvent.max_age) eqs.push(`max_age=${sideEvent.max_age}`);
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowlers/search?${eqs.join('&')}`, { headers: authHeader() })
        .then(r => setSearchResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [search, leagueId, selectedBowler]);

  useEffect(() => {
    clearTimeout(groupSearchTimeout.current);
    if (!groupSearch.trim()) { setGroupResults([]); return; }
    groupSearchTimeout.current = setTimeout(() => {
      const eqs: string[] = [`query=${encodeURIComponent(groupSearch)}`];
      if (sideEvent.gender === 'F') eqs.push('gender=F');
      if (sideEvent.min_age) eqs.push(`min_age=${sideEvent.min_age}`);
      if (sideEvent.max_age) eqs.push(`max_age=${sideEvent.max_age}`);
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowler-group/search?${eqs.join('&')}`, { headers: authHeader() })
        .then(r => setGroupResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(groupSearchTimeout.current);
  }, [groupSearch, leagueId]);

  const handleSelectBowler = (b: Bowler) => {
    setSelectedBowler(b);
    setSearch(b.name);
    setSearchResults([]);
    setGroupSearch(''); setGroupResults([]);
    setQty(1);
    setAddError('');
  };

  const handleAddEntries = async () => {
    if (!selectedBowler || qty < 1) return;
    setAdding(true); setAddError('');
    try {
      await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/bracket-entries`,
        { bowlerId: selectedBowler.id, entries: qty }, { headers: authHeader() });
      setSearch(''); setSelectedBowler(null); setQty(1);
      setGroupSearch(''); setGroupResults([]);
      loadData(); onRefresh();
    } catch (e: any) {
      setAddError(e?.response?.data?.message || 'Failed to add entries');
    } finally { setAdding(false); }
  };

  const removeAllEntriesForBowler = async (bowlerId: number) => {
    if (!window.confirm('Remove ALL bracket entries for this bowler?')) return;
    setRemovingBowler(bowlerId);
    try {
      await axios.delete(`${API}/bookie-league-management/side-events/${sideEvent.id}/bracket-entries/bowler`,
        { data: { bowlerId }, headers: authHeader() });
      loadData(); onRefresh();
    } catch { } finally { setRemovingBowler(null); }
  };

  const generateBrackets = async () => {
    setGenerating(true); setGenerateError(''); setGenerateMsg('');
    try {
      const res = await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/generate-brackets`,
        {}, { headers: authHeader() });
      setGenerateMsg(res.data?.message || 'Brackets generated!');
      loadBrackets(); loadData(); onRefresh();
    } catch (e: any) {
      setGenerateError(e?.response?.data?.error || 'Failed to generate brackets');
      // Reload brackets + data to surface any refunds — do NOT call onRefresh()
      // because that can remount this component and clear the error message.
      loadBrackets(); loadData();
    } finally { setGenerating(false); }
  };

  const deleteBrackets = async () => {
    if (!window.confirm('Delete all generated brackets? This cannot be undone.')) return;
    setDeletingBrackets(true);
    try {
      await axios.delete(`${API}/bookie-league-management/side-events/${sideEvent.id}/delete-brackets`, { headers: authHeader() });
      setBracketsDisplay([]); setBracketsExist(false); setCashers([]);
      loadData();
    } catch { } finally { setDeletingBrackets(false); }
  };

  const toggleBracketPublished = async () => {
    setTogglingPublish(true);
    const newPublished = bracketPublished === 1 ? 0 : 1;
    try {
      await axios.put(`${API}/bookie-league-management/side-events/${sideEvent.id}/bracket-publish`,
        { published: newPublished }, { headers: authHeader() });
      setBracketPublished(newPublished);
    } catch { } finally { setTogglingPublish(false); }
  };

  const pullScores = async () => {
    setPulling(true); setPullMsg('');
    try {
      const res = await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/pull-scores-to-brackets`,
        {}, { headers: authHeader() });
      setPullMsg(res.data?.message || `Advanced ${res.data?.advancedCount ?? 0} round(s)`);
      loadBrackets();
    } catch (e: any) {
      setPullMsg(e?.response?.data?.message || 'Error pulling scores');
    } finally { setPulling(false); }
  };

  const pullScoresWithHandicap = async () => {
    setPullingHdcp(true); setPullMsg('');
    try {
      const res = await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/pull-scores-to-brackets`,
        { useHandicap: true }, { headers: authHeader() });
      setPullMsg(res.data?.message || `Advanced ${res.data?.advancedCount ?? 0} round(s) (w/ handicap)`);
      loadBrackets();
    } catch (e: any) {
      setPullMsg(e?.response?.data?.message || 'Error pulling scores');
    } finally { setPullingHdcp(false); }
  };

  const toggleBracket = (id: number) => setExpandedBrackets(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const handlePrintBrackets = () => printBrackets(sideEvent.name, bracketsDisplay, totalRounds, bracketCost);
  const handlePrintCashers = () => printBracketCashers(sideEvent.name, cashers, refunds, bracketCost);
  const handlePrintBracketEntries = () => printBracketEntries(sideEvent.name, entries, bracketsDisplay);
  const handlePrintAliveBowlers = () => printAliveBowlers(sideEvent.name, bracketsDisplay);

  const totalPot = totalEntries * Number(sideEvent.cost);

  return (
    <div>
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{totalEntries} total entries</span>
          {totalPot > 0 && <span className="text-green-400 font-semibold text-sm">${totalPot.toFixed(2)} pot</span>}
        </div>
        {bracketsExist && (
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={pullScores} disabled={pulling}
              className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50">
              {pulling && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              Pull Scores to Brackets
            </button>
            <button onClick={pullScoresWithHandicap} disabled={pullingHdcp}
              className="text-xs px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50">
              {pullingHdcp && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              Pull Scores w/ Hdcp
            </button>
            <button onClick={handlePrintBrackets}
              className="text-xs px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg">
              Print Brackets
            </button>
            <button onClick={handlePrintCashers}
              className="text-xs px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg">
              Print Cashers
            </button>
            <button onClick={handlePrintBracketEntries}
              className="text-xs px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg">
              Print Entries
            </button>
            <button onClick={handlePrintAliveBowlers}
              className="text-xs px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg">
              Print Alive
            </button>
            <button onClick={deleteBrackets} disabled={deletingBrackets}
              className="text-xs px-3 py-1.5 bg-red-600/80 hover:bg-red-600 text-white rounded-lg disabled:opacity-50">
              {deletingBrackets ? '...' : 'Delete Brackets'}
            </button>
            <button onClick={toggleBracketPublished} disabled={togglingPublish}
              className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50 ${bracketPublished === 1 ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'}`}>
              {togglingPublish
                ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : bracketPublished === 1
                  ? <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
              }
              {bracketPublished === 1 ? 'Published' : 'Publish Brackets'}
            </button>
          </div>
        )}
      </div>

      {pullMsg && <div className="mb-3 text-xs px-3 py-2 bg-blue-900/30 border border-blue-600/40 text-blue-300 rounded-lg">{pullMsg}</div>}
      {generateMsg && <div className="mb-3 text-xs px-3 py-2 bg-green-900/30 border border-green-600/40 text-green-300 rounded-lg">{generateMsg}</div>}
      {generateError && <div className="mb-3 text-xs px-3 py-2 bg-red-900/30 border border-red-600/40 text-red-300 rounded-lg">{generateError}</div>}

      {/* Add entries — only when no brackets yet */}
      {!bracketsExist && (
        <div className="mb-4 p-3 bg-gray-700/40 border border-gray-600/40 rounded-xl space-y-3">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Add Bracket Entries</p>
          <div className="grid grid-cols-2 gap-3">
            {/* Search from my bowlers */}
            <div>
              <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">To add an entry — Search from my bowlers</label>
              <div className="relative">
                <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)}
                  placeholder="Type name, id or email..."
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
                {groupSearch && (
                  <button onClick={() => { setGroupSearch(''); setGroupResults([]); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {groupResults.length > 0 && (
                  <div className="absolute z-20 top-full mt-1 w-full border border-gray-600 rounded-lg shadow-xl overflow-hidden bg-gray-800">
                    {groupResults.map(b => (
                      <button key={b.id} onClick={() => handleSelectBowler(b)}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-700 transition-colors text-left border-b border-gray-700 last:border-0">
                        <div>
                          <div className="text-white text-sm font-medium">{b.name}</div>
                          <div className="text-gray-400 text-xs">{b.email}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* To add an entry — all bowlers */}
            <div>
              <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">To add an entry — Search from all bowlers</label>
              <div className="relative">
                <input value={search} onChange={e => { setSearch(e.target.value); setSelectedBowler(null); }}
                  placeholder="Search bowler..."
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
                {searchResults.length > 0 && (
                  <div className="absolute z-20 top-full mt-1 w-full border border-gray-600 rounded-lg shadow-xl overflow-hidden bg-gray-800">
                    {searchResults.map(b => (
                      <button key={b.id} onClick={() => handleSelectBowler(b)}
                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-700 transition-colors text-left border-b border-gray-700 last:border-0">
                        <div>
                          <div className="text-white text-sm font-medium">{b.name}</div>
                          <div className="text-gray-400 text-xs">{b.email}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {selectedBowler && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-400">Qty</label>
                <input type="number" min={1} max={50} value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value)))}
                  onFocus={e => e.target.select()}
                  className="w-20 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm text-center focus:outline-none focus:border-purple-500" />
              </div>
              {Number(sideEvent.cost) > 0 && (
                <div className="text-sm text-green-400 font-semibold">
                  Charge: ${(qty * Number(sideEvent.cost)).toFixed(2)}
                </div>
              )}
              <button onClick={handleAddEntries} disabled={adding}
                className="ml-auto text-xs px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                {adding && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Add &amp; Charge
              </button>
            </div>
          )}
          {addError && <div className="text-red-400 text-xs">{addError}</div>}
        </div>
      )}

      {/* Generate button */}
      {!bracketsExist && totalEntries >= 2 && (
        <div className="mb-4">
          <button onClick={generateBrackets} disabled={generating}
            className="w-full py-2.5 bg-purple-700 hover:bg-purple-600 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {generating && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {generating ? 'Generating…' : 'Generate Brackets'}
          </button>
        </div>
      )}

      {loading && <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>}

      {/* Entry list */}
      {!loading && entries.length === 0 && !bracketsExist && (
        <div className="text-center text-gray-500 py-8 text-sm">No bracket entries yet.</div>
      )}
      {!loading && entries.length > 0 && (
        <div className="mb-5 border-2 border-indigo-600/50 rounded-xl overflow-hidden">
          <button onClick={() => setShowEntries(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-indigo-900/40 hover:bg-indigo-900/60 transition-colors">
            <div className="flex items-center gap-3">
              <svg className={`w-5 h-5 text-indigo-300 flex-shrink-0 transition-transform ${showEntries ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-sm font-bold text-indigo-200">
                Enrolled Bowlers &mdash; {entries.length} bowlers &middot; {totalEntries} total entries
              </span>
            </div>
            <span className="text-xs text-indigo-400">{showEntries ? 'Click to collapse' : 'Click to expand'}</span>
          </button>
          {showEntries && (
            <div className="p-3 space-y-3 bg-gray-800/40">
              {/* Find Enrolled Bowler search */}
              <div>
                <label className="block text-sm font-bold text-white mb-2">Find Enrolled Bowler</label>
                <input value={entrySearch} onChange={e => setEntrySearch(e.target.value)}
                  placeholder="Search enrolled bowlers..."
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-indigo-500" />
              </div>
              <div className="space-y-1.5">
                {[...entries]
                  .sort((a, b) => _lastName(a.name).localeCompare(_lastName(b.name)))
                  .filter(e => !entrySearch.trim() || e.name.toLowerCase().includes(entrySearch.toLowerCase()))
                  .map(entry => (
                    <div key={entry.bowler_id} className="flex items-center justify-between bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2.5">
                      <div>
                        <div className="text-white text-sm font-medium">{_fmtName(entry.name)}</div>
                        <div className="text-xs text-purple-300">{entry.entryCount} entries &middot; ${(entry.entryCount * Number(sideEvent.cost)).toFixed(2)}</div>
                      </div>
                      {!bracketsExist && (
                        <button onClick={() => removeAllEntriesForBowler(entry.bowler_id)} disabled={removingBowler === entry.bowler_id}
                          title="Remove all entries for this bowler"
                          className="text-gray-400 hover:text-red-400 transition-colors p-1 disabled:opacity-50">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))
                }
                {entrySearch.trim() && [...entries].filter(e => e.name.toLowerCase().includes(entrySearch.toLowerCase())).length === 0 && (
                  <p className="text-center text-gray-500 py-4 text-sm">No enrolled bowlers match your search.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bracket tree display */}
      {bracketsExist && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">{bracketsDisplay.length} Brackets</p>
            <div className="flex gap-2">
              <button onClick={() => setExpandedBrackets(new Set(bracketsDisplay.map(b => b.id)))}
                className="text-[11px] text-gray-400 hover:text-white">Expand All</button>
              <span className="text-gray-600">·</span>
              <button onClick={() => setExpandedBrackets(new Set())}
                className="text-[11px] text-gray-400 hover:text-white">Collapse All</button>
            </div>
          </div>
          {loadingBrackets && <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>}
          {!loadingBrackets && bracketsDisplay.map(b => (
            <div key={b.id} className="border border-gray-600/60 rounded-xl overflow-hidden">
              <button onClick={() => toggleBracket(b.id)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-700/60 hover:bg-gray-700 transition-colors">
                <span className="text-sm font-semibold text-white">Bracket #{b.bracket_number}</span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedBrackets.has(b.id) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedBrackets.has(b.id) && (
                <div className="p-3 bg-gray-800/50">
                  <BracketTree bracket={b} totalRounds={totalRounds} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Cashers */}
      {cashers.length > 0 && (
        <div className="mt-5 border-t border-gray-600/40 pt-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-2">Cashers</p>
          <div className="space-y-1.5">
            {cashers.map((c, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-700/40 rounded-lg px-3 py-2">
                <span className="text-sm text-white">{c.bowler_name}</span>
                <span className="text-green-400 font-semibold text-sm">${Number(c.total_amount).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Clean Game Panel ─────────────────────────────────────────────────── */
const CleanGamePanel: React.FC<{ sideEvent: SideEvent; leagueId: number; onRefresh: () => void }> = ({ sideEvent, leagueId, onRefresh }) => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Bowler[]>([]);
  const [addingBowler, setAddingBowler] = useState<number | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const [payoutModal, setPayoutModal] = useState<{ bowlerId: number; bowlerName: string } | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [groupSearch, setGroupSearch] = useState('');
  const [groupResults, setGroupResults] = useState<Bowler[]>([]);
  const groupSearchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`, { headers: authHeader() });
      setEntries(res.data || []);
    } catch { } finally { setLoading(false); }
  }, [sideEvent.id]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    if (!search.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(() => {
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowlers/search?query=${encodeURIComponent(search)}`, { headers: authHeader() })
        .then(r => setSearchResults(r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [search, leagueId]);

  useEffect(() => {
    clearTimeout(groupSearchTimeout.current);
    if (!groupSearch.trim()) { setGroupResults([]); return; }
    groupSearchTimeout.current = setTimeout(() => {
      axios.get(`${API}/bookie-league-management/leagues/${leagueId}/bowler-group/search?query=${encodeURIComponent(groupSearch)}`, { headers: authHeader() })
        .then(r => setGroupResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(groupSearchTimeout.current);
  }, [groupSearch, leagueId]);

  const addEntry = async (bowler: Bowler) => {
    setAddingBowler(bowler.id);
    try {
      await axios.post(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`,
        { bowlerId: bowler.id, paid: true }, { headers: authHeader() });
      setSearch(''); setSearchResults([]); setGroupSearch(''); setGroupResults([]);
      loadEntries(); onRefresh();
    } catch { } finally { setAddingBowler(null); }
  };

  const removeEntry = async (entry: Entry) => {
    setRemoving(entry.bowler_id);
    try {
      await axios.delete(`${API}/bookie-league-management/side-events/${sideEvent.id}/entries`,
        { data: { bowlerId: entry.bowler_id }, headers: authHeader() });
      loadEntries(); onRefresh();
    } catch { } finally { setRemoving(null); }
  };

  const totalPot = entries.length * Number(sideEvent.cost);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-400">{entries.length} enrolled</span>
        {Number(sideEvent.cost) > 0 && <span className="text-green-400 font-semibold text-sm">${totalPot.toFixed(2)} pot</span>}
      </div>

      {/* Search & Add */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Search from my bowlers */}
        <div>
          <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Search from my bowlers</label>
          <div className="relative">
            <input value={groupSearch} onChange={e => setGroupSearch(e.target.value)}
              placeholder="Type name, id or email..."
              className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
            {groupSearch && (
              <button onClick={() => { setGroupSearch(''); setGroupResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {groupResults.length > 0 && (
              <div className="absolute z-20 top-full mt-1 w-full border border-gray-600 rounded-lg shadow-xl overflow-hidden" style={{ backgroundColor: '#2d3748' }}>
                {groupResults.map(b => (
                  <button key={b.id} onClick={() => addEntry(b)} disabled={addingBowler === b.id}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-600 transition-colors text-left border-b border-gray-600 last:border-0">
                    <div>
                      <div className="text-white text-sm font-medium">{b.name}</div>
                      <div className="text-gray-400 text-xs">{b.email}</div>
                    </div>
                    {Number(sideEvent.cost) > 0 && <span className="text-green-400 text-xs font-semibold ml-2">${Number(sideEvent.cost).toFixed(2)}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Search from all bowlers */}
        <div>
          <label className="block text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">Search from all bowlers</label>
          <div className="relative">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search bowler by name..."
              className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500" />
            {search && (
              <button onClick={() => { setSearch(''); setSearchResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {searchResults.length > 0 && (
              <div className="absolute z-20 top-full mt-1 w-full border border-gray-600 rounded-lg shadow-xl overflow-hidden" style={{ backgroundColor: '#2d3748' }}>
                {searchResults.map(b => (
                  <button key={b.id} onClick={() => addEntry(b)} disabled={addingBowler === b.id}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-600 transition-colors text-left border-b border-gray-600 last:border-0">
                    <div>
                      <div className="text-white text-sm font-medium">{b.name}</div>
                      <div className="text-gray-400 text-xs">{b.email}</div>
                    </div>
                    {Number(sideEvent.cost) > 0 && <span className="text-green-400 text-xs font-semibold ml-2">${Number(sideEvent.cost).toFixed(2)}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>}
      {!loading && entries.length === 0 && (
        <div className="text-center text-gray-500 py-8 text-sm">No entries yet. Search above to add bowlers.</div>
      )}
      {!loading && (
        <div className="space-y-1.5">
          {entries.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between rounded-lg px-3 py-2.5 border bg-gray-700/50 border-gray-600/50 transition-colors">
              <div className="flex items-center gap-2.5">
                <span className="w-2 h-2 rounded-full bg-gray-500 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-white">{entry.bowler_name}</div>
                  {entry.paid
                    ? <div className="text-xs text-green-400">Paid · ${Number(sideEvent.cost).toFixed(2)}</div>
                    : <div className="text-xs text-yellow-400">Unpaid</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPayoutModal({ bowlerId: entry.bowler_id, bowlerName: entry.bowler_name })}
                  className="px-3 py-1.5 bg-green-600/80 hover:bg-green-600 text-white text-xs rounded-md font-semibold flex items-center gap-1.5 transition-colors">
                  💰 Pay Out
                </button>
                <button onClick={() => removeEntry(entry)} disabled={removing === entry.bowler_id}
                  className="text-gray-400 hover:text-red-400 transition-colors p-1 disabled:opacity-50">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {payoutModal && (
        <BookiePayoutModal
          isOpen={true}
          onClose={() => setPayoutModal(null)}
          bowlerId={payoutModal.bowlerId}
          bowlerName={payoutModal.bowlerName}
          sideEventId={sideEvent.id}
          sideEventType="clean_game"
          leagueId={leagueId}
          defaultAmount={totalPot}
          onPayoutComplete={() => setPayoutModal(null)}
        />
      )}
    </div>
  );
};

/* ─── Doubles Payout Modal ────────────────────────────────────────────────── */
const DoublesPayoutModal: React.FC<{
  bowlers: { bowler_id: number; bowler_name: string }[];
  sideEventId: number;
  leagueId: number;
  onClose: () => void;
  onComplete?: () => void;
}> = ({ bowlers, sideEventId, leagueId, onClose, onComplete }) => {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [statuses, setStatuses] = useState<Record<number, 'idle' | 'paying' | 'paid' | 'error'>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const parsedAmount = parseFloat(amount);
  const canSubmit = !isNaN(parsedAmount) && parsedAmount > 0 &&
    !Object.values(statuses).some(s => s === 'paying');
  const allPaid = bowlers.length > 0 && bowlers.every(b => statuses[b.bowler_id] === 'paid');

  const payBowler = async (bowler: { bowler_id: number; bowler_name: string }) => {
    setStatuses(prev => ({ ...prev, [bowler.bowler_id]: 'paying' }));
    setGlobalError(null);
    try {
      await axios.post(
        `${API}/admin/payouts/sidepot`,
        {
          sideEventId,
          leagueId,
          recipientType: 'bowler',
          recipientId: bowler.bowler_id,
          amount: parsedAmount,
          sideEventType: 'side_event',
          placement: 1,
          notes: notes.trim() || `Doubles payout`,
        },
        { headers: authHeader() }
      );
      setStatuses(prev => ({ ...prev, [bowler.bowler_id]: 'paid' }));
    } catch (e: any) {
      setStatuses(prev => ({ ...prev, [bowler.bowler_id]: 'error' }));
      setGlobalError(e?.response?.data?.message || e?.response?.data?.error || `Failed to pay ${bowler.bowler_name}`);
    }
  };

  const handlePayBoth = async () => {
    for (const b of bowlers) {
      if (statuses[b.bowler_id] !== 'paid') await payBowler(b);
    }
    onComplete?.();
  };

  useEffect(() => {
    if (allPaid) setTimeout(onClose, 1800);
  }, [allPaid, onClose]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={e => { if (e.target === e.currentTarget && canSubmit) onClose(); }}>
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Pay Double</h2>
          <button onClick={onClose} disabled={!canSubmit}
            className="text-gray-400 hover:text-white transition-colors p-1 disabled:opacity-40">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {allPaid ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-3">✅</div>
            <p className="text-green-400 font-semibold text-lg">Both paid out!</p>
            <p className="text-gray-400 text-sm mt-1">${parsedAmount.toFixed(2)} each</p>
          </div>
        ) : (
          <>
            {/* Bowler rows */}
            <div className="space-y-2 mb-5">
              {bowlers.map(b => {
                const st = statuses[b.bowler_id] ?? 'idle';
                return (
                  <div key={b.bowler_id}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors
                      ${st === 'paid' ? 'bg-green-900/30 border-green-700/50' :
                        st === 'error' ? 'bg-red-900/30 border-red-700/50' :
                        'bg-gray-700/50 border-gray-600/50'}`}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-purple-600/60 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                        {b.bowler_name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-white text-sm font-medium">{b.bowler_name}</span>
                    </div>
                    <div>
                      {st === 'paid' && <span className="text-green-400 text-sm font-semibold">✔ Paid</span>}
                      {st === 'paying' && <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />}
                      {st === 'error' && (
                        <button onClick={() => payBowler(b)} disabled={isNaN(parsedAmount) || parsedAmount <= 0}
                          className="text-xs px-2 py-1 bg-red-700 hover:bg-red-600 text-white rounded font-medium">
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Amount per person */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Amount per bowler</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">$</span>
                <input type="number" min="0" step="0.01" value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="0.00" autoFocus
                  className="w-full pl-7 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 placeholder-gray-500" />
              </div>
              {!isNaN(parsedAmount) && parsedAmount > 0 && (
                <p className="text-[11px] text-gray-500 mt-1">Total: ${(parsedAmount * bowlers.length).toFixed(2)} for {bowlers.length} bowler{bowlers.length !== 1 ? 's' : ''}</p>
              )}
            </div>

            {/* Notes */}
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Notes <span className="text-gray-600">(optional)</span></label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="e.g. Series winner, Squad 1…"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 resize-none placeholder-gray-500" />
            </div>

            {globalError && (
              <div className="mb-4 px-3 py-2 bg-red-900/40 border border-red-500/40 rounded-lg text-red-300 text-xs">{globalError}</div>
            )}

            <div className="flex gap-3">
              <button onClick={onClose} disabled={!canSubmit}
                className="flex-1 px-4 py-2.5 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handlePayBoth} disabled={!canSubmit}
                className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                <span>💰</span> Pay Both
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── SE Live Panel (survivor / eliminator / mystery / side_pots) ──────── */
const SELivePanel: React.FC<{ sideEvent: SideEvent; squadId: number; refreshKey: number }> = ({ sideEvent, squadId, refreshKey }) => {
  const [seResult, setSeResult] = useState<any>(null);
  const [loadingResults, setLoadingResults] = useState(true);
  const [mysteryInput, setMysteryInput] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [editing, setEditing] = useState<{ gameNumber: number; field: string; value: string } | null>(null);
  const [payoutModal, setPayoutModal] = useState<{ bowlerId: number; bowlerName: string } | null>(null);
  const [doublesPayoutModal, setDoublesPayoutModal] = useState<{ bowlers: { bowler_id: number; bowler_name: string }[] } | null>(null);
  const [matchingBowlers, setMatchingBowlers] = useState<any[]>([]);
  const [currentTarget, setCurrentTarget] = useState<number | null>(null);
  const [useHandicap, setUseHandicap] = useState(!!sideEvent.hdcp);
  const [awaPrintN, setAwaPrintN] = useState('10');
  const [paidBowlerIds, setPaidBowlerIds] = useState<Set<number>>(new Set());

  const findMatches = useCallback(async (target: number) => {
    const isSeries = sideEvent.type === 'mystery_series';
    const gameNums: number[] = sideEvent.applies_to_game_numbers
      ? sideEvent.applies_to_game_numbers.split(',').map((n: string) => parseInt(n.trim())).filter(Boolean)
      : [];
    try {
      const r = await axios.post(
        `${API}/bookie-scores/squads/${squadId}/find-mystery`,
        { target, type: isSeries ? 'series' : 'score', gameNumbers: gameNums },
        { headers: authHeader() }
      );
      setMatchingBowlers(r.data.matching_bowlers || []);
      setCurrentTarget(target);
    } catch { setMatchingBowlers([]); }
  }, [sideEvent.type, sideEvent.applies_to_game_numbers, squadId]);

  const loadData = useCallback(async () => {
    setLoadingResults(true);
    try {
      const hdcpParam = useHandicap ? '?useHandicap=true' : '';
      const [resultsRes, metaRes] = await Promise.all([
        axios.get(`${API}/bookie-scores/squads/${squadId}/side-event-results${hdcpParam}`, { headers: authHeader() }),
        ['mystery_score', 'mystery_series'].includes(sideEvent.type)
          ? axios.get(`${API}/bookie-league-management/side-events/${sideEvent.id}/custom-meta`, { headers: authHeader() })
          : Promise.resolve({ data: {} }),
      ]);
      const found = resultsRes.data.find((r: any) => r.id === sideEvent.id) || null;
      setSeResult(found);
      // Determine saved target and populate input + run match query
      const savedTarget = metaRes.data?.mystery_target;
      if (savedTarget !== undefined && savedTarget !== null) {
        const t = Number(savedTarget);
        setMysteryInput(String(t));
        findMatches(t);
      }
    } catch { } finally { setLoadingResults(false); }
  }, [sideEvent.id, sideEvent.type, squadId, findMatches, useHandicap]);

  const loadPaidBowlers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/payouts/side-event/${sideEvent.id}/paid-bowlers`, { headers: authHeader() });
      setPaidBowlerIds(new Set(r.data as number[]));
    } catch { /* non-critical */ }
  }, [sideEvent.id]);

  useEffect(() => { loadData(); }, [loadData, refreshKey]);
  useEffect(() => { loadPaidBowlers(); }, [loadPaidBowlers]);

  const saveMysteryTarget = async () => {
    const v = parseInt(mysteryInput, 10);
    if (isNaN(v)) return;
    setSavingMeta(true);
    try {
      await axios.put(`${API}/bookie-league-management/side-events/${sideEvent.id}/custom-meta`, { mystery_target: v }, { headers: authHeader() });
      // Directly query matches — no need to re-fetch the whole results batch
      await findMatches(v);
    } catch { } finally { setSavingMeta(false); }
  };

  const rollRandom = () => {
    const min = sideEvent.type === 'mystery_series' ? 300 : 100;
    const max = sideEvent.type === 'mystery_series' ? 900 : 299;
    setMysteryInput(String(Math.floor(Math.random() * (max - min + 1)) + min));
  };

  const startEdit = (gameNumber: number, field: string, val: number) =>
    setEditing({ gameNumber, field, value: String(val) });

  const saveConfig = async () => {
    if (!editing) return;
    const num = parseFloat(editing.value);
    if (isNaN(num)) { setEditing(null); return; }
    try {
      const url = sideEvent.type === 'survivor'
        ? `${API}/bookie-league-management/side-events/${sideEvent.id}/survivor-config/${editing.gameNumber}`
        : `${API}/bookie-league-management/side-events/${sideEvent.id}/eliminator-config/${editing.gameNumber}`;
      await axios.put(url, { [editing.field]: num }, { headers: authHeader() });
      setEditing(null);
      loadData();
    } catch { setEditing(null); }
  };

  const printAWA = () => {
    const n = Math.min(100, Math.max(2, parseInt(awaPrintN) || 10));
    const pots: any[] = seResult?.pot_results || [];
    if (pots.length === 0) { alert('No scores entered yet.'); return; }
    const potSections = pots.map((pot: any) => {
      const label = pot.config_type === 'game_series' ? `Series (G${pot.game_identifier})` : `Game ${pot.game_identifier}`;
      const entries = (pot.all_entries || []).slice(0, n);
      if (entries.length === 0) return `<div style="margin-bottom:16px;"><h3 style="font-size:13px;margin:0 0 6px;">${label}</h3><em style="color:#9ca3af;">No scores entered</em></div>`;
      const rows = entries.map((b: any) => {
        const isPaid = pot.is_doubles
          ? (b.bowler_ids || []).some((bi: any) => paidBowlerIds.has(bi.bowler_id))
          : paidBowlerIds.has(b.bowler_id);
        const paidMark = isPaid ? ' <span style="display:inline-block;background:#fef3c7;color:#92400e;font-size:9px;font-weight:600;padding:1px 5px;border-radius:3px;margin-left:4px;">✓ PAID</span>' : '';
        return `<tr><td style="padding:4px 10px;border-bottom:1px solid #e5e7eb;">${b.rank}.</td><td style="padding:4px 10px;border-bottom:1px solid #e5e7eb;">${b.bowler_name}${paidMark}</td><td style="padding:4px 10px;border-bottom:1px solid #e5e7eb;font-weight:600;">${b.score}</td></tr>`;
      }).join('');
      return `<div style="margin-bottom:16px;">
        <h3 style="font-size:13px;margin:0 0 6px;">${label}</h3>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead><tr>
            <th style="background:#f3f4f6;padding:4px 10px;text-align:left;border-bottom:2px solid #d1d5db;">#</th>
            <th style="background:#f3f4f6;padding:4px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Name</th>
            <th style="background:#f3f4f6;padding:4px 10px;text-align:left;border-bottom:2px solid #d1d5db;">Score</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
    }).join('<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0;"/>');
    const html = `<!DOCTYPE html><html><head><title>${sideEvent.name} – Top ${n} Placements</title><style>body{font-family:sans-serif;padding:24px;color:#111;}</style></head><body><h2 style="margin-bottom:4px;">${sideEvent.name}</h2><p style="color:#6b7280;font-size:13px;margin-bottom:20px;">Placements 1 through ${n}</p>${potSections}<p style="color:#9ca3af;font-size:11px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p></body></html>`;
    const w = window.open('', '_blank', 'width=820,height=700');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
  };

  const printLiveRounds = () => {
    if (!seResult) return;
    const isSurvivor = sideEvent.type === 'survivor';
    const roundsHTML = (seResult.computed_rounds || []).map((r: any) => {
      const threshold = isSurvivor
        ? `Min Score: <strong>${r.survivor_score}</strong>`
        : `Advance Top: <strong>${r.advancement_percentage}%</strong>`;
      const cashers = isSurvivor
        ? (r.survivor_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ')
        : (r.advancer_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ');
      const elim = isSurvivor
        ? (r.eliminated_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ')
        : (r.eliminated_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ');
      return `<tr>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">Game ${r.game_number}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;">${threshold}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#15803d;">${cashers || '—'}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#b91c1c;">${elim || '—'}</td>
      </tr>`;
    }).join('');
    const finalCashers = (seResult.cashers || []).map((b: any) => b.bowler_name).join(', ') || 'None yet';
    const html = `<!DOCTYPE html><html><head><title>${sideEvent.name} – Live Rounds</title>
      <style>body{font-family:sans-serif;padding:24px;}table{width:100%;border-collapse:collapse;}th{background:#f3f4f6;padding:6px 10px;text-align:left;border-bottom:2px solid #d1d5db;}</style>
      </head><body>
      <h2>${sideEvent.name}</h2>
      <p style="color:#6b7280;font-size:13px;">Live ${isSurvivor ? 'Survivor' : 'Eliminator'} Rounds</p>
      <table><thead><tr><th>Game</th><th>Threshold</th><th style="color:#15803d;">Survived / Advancing</th><th style="color:#b91c1c;">Eliminated</th></tr></thead>
      <tbody>${roundsHTML}</tbody></table>
      <div style="margin-top:20px;padding:12px;background:#fefce8;border:1px solid #ca8a04;border-radius:6px;">
        <strong>Final Cashers:</strong> ${finalCashers}
      </div>
      <p style="color:#9ca3af;font-size:11px;margin-top:20px;">Printed ${new Date().toLocaleString()}</p>
      </body></html>`;
    const w = window.open('', '_blank', 'width=820,height=600');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
  };

  const toggleHandicap = () => {
    const next = !useHandicap;
    setUseHandicap(next);
    axios.patch(`${API}/bookie-league-management/side-events/${sideEvent.id}/hdcp`,
      { hdcp: next }, { headers: authHeader() }).catch(() => {});
  };

  if (loadingResults) return (
    <div className="mt-6 border-t border-gray-700 pt-4 flex justify-center">
      <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ── SURVIVOR ────────────────────────────────────────────────────────────────
  if (sideEvent.type === 'survivor') {
    const rounds: any[] = seResult?.computed_rounds || seResult?.config?.map((c: any) => ({ game_number: c.game_number, survivor_score: c.survivor_score, round_payout: c.round_payout, survivor_list: [], eliminated_list: [], pending_list: [], survivors_count: 0, eliminated_count: 0 })) || [];
    return (
      <div className="mt-6 border-t border-gray-700 pt-5">
        <SideEventPayoutPresets
          sideEvent={sideEvent}
          leagueId={sideEvent.league_id}
          seResult={seResult}
          presetsApiBase={`${API}/bookie-league-management/side-events/${sideEvent.id}`}
          paidBowlerIds={paidBowlerIds}
          onPayoutComplete={() => { loadData(); loadPaidBowlers(); }}
        />
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-emerald-300 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            Live Survivor Rounds
          </h4>
          <div className="flex items-center gap-2">
            <button onClick={toggleHandicap}
              className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${useHandicap ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
              Hdcp {useHandicap ? 'ON' : 'OFF'}
            </button>
            <button onClick={printLiveRounds} className="text-xs px-2.5 py-1 bg-blue-700/60 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
              Print Results
            </button>
          </div>
        </div>
        {rounds.length === 0 && <p className="text-xs text-gray-500 text-center py-4">No rounds configured. Check your game numbers.</p>}
        {rounds.map((r: any) => (
          <div key={r.game_number} className="mb-3 bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white">Game {r.game_number}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400">Min Score:</span>
                {editing?.gameNumber === r.game_number && editing?.field === 'survivor_score'
                  ? <input type="number" value={editing.value} autoFocus
                      onChange={e => setEditing(ex => ({ ...ex!, value: e.target.value }))}
                      onFocus={e => e.target.select()}
                      onBlur={saveConfig} onKeyDown={e => { if (e.key === 'Enter') saveConfig(); if (e.key === 'Escape') setEditing(null); }}
                      className="w-16 text-center bg-gray-600 border border-purple-500 rounded px-1.5 py-0.5 text-xs text-white focus:outline-none" />
                  : <button onClick={() => startEdit(r.game_number, 'survivor_score', r.survivor_score)}
                      className="text-xs font-bold text-purple-300 hover:text-yellow-300 bg-gray-600/60 px-2 py-0.5 rounded cursor-pointer transition-colors">
                      {r.survivor_score}
                    </button>
                }
                {r.round_payout > 0 && <span className="text-[10px] text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">${r.round_payout}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs mb-2">
              <span className="text-emerald-400">{r.survivors_count} survived</span>
              {r.eliminated_count > 0 && <span className="text-red-400">{r.eliminated_count} out</span>}
              {r.pending_list?.length > 0 && <span className="text-gray-500">{r.pending_list.length} pending</span>}
            </div>
            {r.survivor_list?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {[...r.survivor_list].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                  <span key={b.bowler_id} className="text-[10px] bg-emerald-900/40 text-emerald-300 px-1.5 py-0.5 rounded">
                    {_fmtName(b.bowler_name)} ({b.score})
                  </span>
                ))}
              </div>
            )}
            {r.eliminated_list?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {[...r.eliminated_list].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                  <span key={b.bowler_id} className="text-[10px] bg-red-900/30 text-red-400/80 px-1.5 py-0.5 rounded line-through">
                    {_fmtName(b.bowler_name)} ({b.score})
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {seResult?.cashers?.length > 0 && (() => {
          const lastSurR = rounds[rounds.length - 1];
          const lastSurScores: Record<number, number> = {};
          for (const b of (lastSurR?.survivor_list || [])) lastSurScores[b.bowler_id] = b.score;
          const sortedSur = [...seResult.cashers].sort((a: any, b: any) => (lastSurScores[b.bowler_id] || 0) - (lastSurScores[a.bowler_id] || 0));
          return (
            <div className="mt-2 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
              <div className="text-xs font-bold text-yellow-300 mb-2">🏆 Final Cashers</div>
              {sortedSur.map((c: any, i: number) => (
                <div key={c.bowler_id} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2 text-xs text-white">
                    <span className="text-gray-500">{i + 1}.</span>
                    <span className="font-medium">{c.bowler_name}</span>
                    {lastSurScores[c.bowler_id] != null && <span className="text-yellow-300 text-[10px] font-semibold">{lastSurScores[c.bowler_id]}</span>}
                    {c.payout > 0 && <span className="text-green-400 text-[10px]">+${c.payout}</span>}
                  </div>
                  {paidBowlerIds.has(c.bowler_id) ? (
                    <span className="ml-3 px-2.5 py-1 bg-gray-700/80 text-green-400 text-[10px] rounded-lg font-semibold">✓ Paid – see Financials</span>
                  ) : (
                    <button
                      onClick={() => setPayoutModal({ bowlerId: c.bowler_id, bowlerName: c.bowler_name })}
                      className="ml-3 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white text-[10px] rounded-lg font-semibold flex items-center gap-1 transition-colors">
                      💰 Pay Out
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
        {payoutModal && (
          <BookiePayoutModal
            isOpen={true}
            onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId}
            bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id}
            sideEventType='survivor'
            leagueId={sideEvent.league_id}
            defaultAmount={0}
            onPayoutComplete={() => { setPayoutModal(null); loadPaidBowlers(); }}
          />
        )}
      </div>
    );
  }

  // ── ELIMINATOR ──────────────────────────────────────────────────────────────
  if (sideEvent.type === 'eliminator') {
    const rounds: any[] = seResult?.computed_rounds || seResult?.config?.map((c: any) => ({ game_number: c.game_number, advancement_percentage: c.advancement_percentage, round_payout: c.round_payout, advancer_list: [], eliminated_list: [], pending_list: [], advancers_count: 0, eliminated_count: 0 })) || [];
    return (
      <div className="mt-6 border-t border-gray-700 pt-5">
        <SideEventPayoutPresets
          sideEvent={sideEvent}
          leagueId={sideEvent.league_id}
          seResult={seResult}
          presetsApiBase={`${API}/bookie-league-management/side-events/${sideEvent.id}`}
          paidBowlerIds={paidBowlerIds}
          onPayoutComplete={() => { loadData(); loadPaidBowlers(); }}
        />
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-orange-300 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-orange-400" />
            Live Eliminator Rounds
          </h4>
          <div className="flex items-center gap-2">
            <button onClick={toggleHandicap}
              className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${useHandicap ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
              Hdcp {useHandicap ? 'ON' : 'OFF'}
            </button>
            <button onClick={printLiveRounds} className="text-xs px-2.5 py-1 bg-blue-700/60 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
              Print Results
            </button>
          </div>
        </div>
        {rounds.length === 0 && <p className="text-xs text-gray-500 text-center py-4">No rounds configured. Check your game numbers.</p>}
        {rounds.map((r: any) => (
          <div key={r.game_number} className="mb-3 bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white">Game {r.game_number}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400">Advance:</span>
                {editing?.gameNumber === r.game_number && editing?.field === 'advancement_percentage'
                  ? <select value={editing.value} autoFocus
                      onChange={e => { setEditing(ex => ({ ...ex!, value: e.target.value })); }}
                      onBlur={saveConfig}
                      style={{ colorScheme: 'dark' }}
                      className="bg-gray-600 border border-purple-500 rounded px-1.5 py-0.5 text-xs text-white focus:outline-none">
                      <option value={25} className="bg-gray-800">1 of 4 (25%)</option>
                      <option value={33} className="bg-gray-800">1 of 3 (33%)</option>
                      <option value={50} className="bg-gray-800">1 of 2 (50%)</option>
                      <option value={67} className="bg-gray-800">2 of 3 (67%)</option>
                      <option value={75} className="bg-gray-800">3 of 4 (75%)</option>
                      <option value={85} className="bg-gray-800">Top 85% (85%)</option>
                      <option value={100} className="bg-gray-800">All (100%)</option>
                    </select>
                  : <button onClick={() => startEdit(r.game_number, 'advancement_percentage', r.advancement_percentage)}
                      className="text-xs font-bold text-purple-300 hover:text-yellow-300 bg-gray-600/60 px-2 py-0.5 rounded cursor-pointer transition-colors">
                      {r.advancement_percentage}%
                    </button>
                }
                {r.cutoff_score && <span className="text-[10px] text-gray-400">cutoff: {r.cutoff_score}</span>}
                {r.round_payout > 0 && <span className="text-[10px] text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">${r.round_payout}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs mb-2">
              <span className="text-orange-400">{r.advancers_count ?? 0} advancing</span>
              {(r.eliminated_count ?? 0) > 0 && <span className="text-red-400">{r.eliminated_count} out</span>}
              {r.pending_list?.length > 0 && <span className="text-gray-500">{r.pending_list.length} pending</span>}
            </div>
            {r.advancer_list?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {[...r.advancer_list].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                  <span key={b.bowler_id} className="text-[10px] bg-orange-900/40 text-orange-300 px-1.5 py-0.5 rounded">
                    {_fmtName(b.bowler_name)} ({b.score})
                  </span>
                ))}
              </div>
            )}
            {r.eliminated_list?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {[...r.eliminated_list].sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0)).map((b: any) => (
                  <span key={b.bowler_id} className="text-[10px] bg-red-900/30 text-red-400/80 px-1.5 py-0.5 rounded line-through">
                    {_fmtName(b.bowler_name)} ({b.score})
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {seResult?.cashers?.length > 0 && (() => {
          const lastElimR = rounds[rounds.length - 1];
          const lastElimScores: Record<number, number> = {};
          for (const b of (lastElimR?.advancer_list || [])) lastElimScores[b.bowler_id] = b.score;
          const sortedElim = [...seResult.cashers].sort((a: any, b: any) => (lastElimScores[b.bowler_id] || 0) - (lastElimScores[a.bowler_id] || 0));
          return (
            <div className="mt-2 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
              <div className="text-xs font-bold text-yellow-300 mb-2">🏆 Final Cashers</div>
              {sortedElim.map((c: any, i: number) => (
                <div key={c.bowler_id} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2 text-xs text-white">
                    <span className="text-gray-500">{i + 1}.</span>
                    <span className="font-medium">{c.bowler_name}</span>
                    {lastElimScores[c.bowler_id] != null && <span className="text-yellow-300 text-[10px] font-semibold">{lastElimScores[c.bowler_id]}</span>}
                    {c.payout > 0 && <span className="text-green-400 text-[10px]">+${c.payout}</span>}
                  </div>
                  {paidBowlerIds.has(c.bowler_id) ? (
                    <span className="ml-3 px-2.5 py-1 bg-gray-700/80 text-green-400 text-[10px] rounded-lg font-semibold">✓ Paid – see Financials</span>
                  ) : (
                    <button
                      onClick={() => setPayoutModal({ bowlerId: c.bowler_id, bowlerName: c.bowler_name })}
                      className="ml-3 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white text-[10px] rounded-lg font-semibold flex items-center gap-1 transition-colors">
                      💰 Pay Out
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
        {payoutModal && (
          <BookiePayoutModal
            isOpen={true}
            onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId}
            bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id}
            sideEventType='eliminator'
            leagueId={sideEvent.league_id}
            defaultAmount={0}
            onPayoutComplete={() => { setPayoutModal(null); loadPaidBowlers(); }}
          />
        )}
      </div>
    );
  }

  // ── MYSTERY SCORE / MYSTERY SERIES ──────────────────────────────────────────
  if (sideEvent.type === 'mystery_score' || sideEvent.type === 'mystery_series') {
    const isSeries = sideEvent.type === 'mystery_series';
    return (
      <div className="mt-6 border-t border-gray-700 pt-5">
        <h4 className="text-sm font-bold text-blue-300 mb-3">
          {isSeries ? 'Mystery Series' : 'Mystery Score'} Target
        </h4>
        <div className="flex items-center gap-2 mb-4">
          <input type="number" value={mysteryInput} onChange={e => setMysteryInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && saveMysteryTarget()}
            placeholder={isSeries ? 'e.g. 550' : 'e.g. 200'}
            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500" />
          <button onClick={rollRandom}
            className="px-3 py-2 bg-indigo-700/70 hover:bg-indigo-700 text-white text-xs rounded-lg font-medium">
            🎲 Random
          </button>
          <button onClick={saveMysteryTarget} disabled={savingMeta}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg font-medium disabled:opacity-50">
            {savingMeta ? '...' : 'Set'}
          </button>
        </div>
        {currentTarget !== null && (
          <p className="text-xs text-blue-400 mb-3">
            Target: <span className="font-bold text-white text-sm">{currentTarget}</span>
            {isSeries ? ' (series total)' : ''}
          </p>
        )}
        {matchingBowlers.length === 0
          ? <div className="text-center text-gray-500 py-6 text-xs">
              {currentTarget !== null
                ? 'No bowlers match the target yet — enter scores and hit Set again.'
                : 'Set a target to check for matches.'}
            </div>
          : <div className="space-y-2">
              <p className="text-xs text-green-400 font-semibold mb-2">{matchingBowlers.length} match{matchingBowlers.length > 1 ? 'es' : ''} found!</p>
              {matchingBowlers.map((b: any) => (
                <div key={b.bowler_id} className="flex items-center justify-between bg-green-900/30 border border-green-500/40 rounded-lg px-3 py-2.5">
                  <div>
                    <div className="text-sm font-medium text-white">{b.bowler_name}</div>
                    <div className="text-green-400 text-xs font-bold">
                      {isSeries ? `${b.total_score} series ✓` : `G${b.game_number}: ${b.score} ✓`}
                    </div>
                  </div>
                  <button
                    onClick={() => setPayoutModal({ bowlerId: b.bowler_id, bowlerName: b.bowler_name })}
                    className="ml-3 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg font-semibold flex items-center gap-1.5 transition-colors">
                    💰 Pay Out
                  </button>
                </div>
              ))}
            </div>
        }
        {payoutModal && (
          <BookiePayoutModal
            isOpen={true}
            onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId}
            bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id}
            sideEventType={sideEvent.type as 'mystery_score' | 'mystery_series'}
            leagueId={sideEvent.league_id}
            defaultAmount={0}
            onPayoutComplete={() => setPayoutModal(null)}
          />
        )}
      </div>
    );
  }

  // ── SIDE POTS / ALL WITH ALL ────────────────────────────────────────────────
  if (sideEvent.type === 'side_pots' || sideEvent.type === 'custom') {
    const pots: any[] = seResult?.pot_results || [];
    return (
      <div className="mt-6 border-t border-gray-700 pt-5">
        <SideEventPayoutPresets
          sideEvent={sideEvent}
          leagueId={sideEvent.league_id}
          seResult={seResult}
          presetsApiBase={`${API}/bookie-league-management/side-events/${sideEvent.id}`}
          paidBowlerIds={paidBowlerIds}
          onPayoutComplete={() => { loadData(); loadPaidBowlers(); }}
        />
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-amber-300">Side Pot Results</h4>
          <button onClick={toggleHandicap}
            className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${useHandicap ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
            Hdcp {useHandicap ? 'ON' : 'OFF'}
          </button>
        </div>
        {sideEvent.type === 'custom' && (
          <div className="flex items-center gap-2 mb-3 p-2 bg-gray-700/40 rounded-lg">
            <span className="text-xs text-gray-300">Print placements 1 through</span>
            <input
              type="number" min={2} max={100} value={awaPrintN}
              onChange={e => setAwaPrintN(e.target.value)}
              className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-white text-center focus:outline-none focus:border-blue-500" />
            <span className="text-[10px] text-gray-500">(min 2 · max 100)</span>
            <button onClick={printAWA}
              className="ml-1 px-2.5 py-1 bg-blue-700/80 hover:bg-blue-700 text-white text-xs rounded-lg flex items-center gap-1 transition-colors">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
              Print
            </button>
          </div>
        )}
        {pots.length === 0 && <p className="text-xs text-gray-500 text-center py-4">No side pot configurations found.</p>}
        {pots.map((pot: any, pi: number) => (
          <div key={pi} className="mb-4 bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">
            <div className="px-3 py-2 bg-gray-700/50 border-b border-gray-600/50 flex items-center justify-between">
              <span className="text-xs font-semibold text-white">
                {pot.config_type === 'game_series' ? `Series (G${pot.game_identifier})` : `Game ${pot.game_identifier}`}
              </span>
              <span className="text-[10px] text-gray-400">Top {pot.winners_pct}% → {pot.winner_count} winner{pot.winner_count !== 1 ? 's' : ''}</span>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {(!pot.all_entries || pot.all_entries.length === 0)
                ? <div className="px-3 py-3 text-xs text-gray-500 text-center">No scores entered yet</div>
                : (() => {
                    const cutoffScore = pot.all_entries[pot.winner_count - 1]?.score ?? null;
                    return pot.all_entries.map((b: any) => {
                      const isWinner = b.rank <= pot.winner_count;
                      const isTied = !isWinner && cutoffScore !== null && b.score === cutoffScore;
                      return (
                        <div key={b.bowler_id} className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0
                          ${isWinner ? 'bg-yellow-900/20 text-yellow-200' : isTied ? 'bg-orange-900/20 text-orange-200' : 'text-gray-400'}`}>
                          <div className="flex items-center gap-1.5">
                            <span>{b.rank}. {b.bowler_name}</span>
                            {isTied && <span className="text-[9px] bg-orange-700/60 text-orange-200 px-1.5 py-0.5 rounded-full font-semibold">TIE</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${isWinner ? 'text-yellow-300' : isTied ? 'text-orange-300' : ''}`}>{b.score}</span>
                            {(isWinner || isTied) && !pot.is_doubles && (
                              paidBowlerIds.has(b.bowler_id)
                                ? <span className="px-2 py-0.5 bg-gray-700/80 text-green-400 text-[10px] rounded font-semibold">✓ Paid</span>
                                : <button
                                    onClick={() => setPayoutModal({ bowlerId: b.bowler_id, bowlerName: b.bowler_name })}
                                    className={`px-2 py-0.5 text-white text-[10px] rounded font-semibold transition-colors
                                      ${isWinner ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}>
                                    💰 Pay Out
                                  </button>
                            )}
                            {(isWinner || isTied) && pot.is_doubles && (b.bowler_ids?.length ?? 0) > 0 && (
                              (b.bowler_ids || []).some((bi: any) => paidBowlerIds.has(bi.bowler_id))
                                ? <span className="px-2 py-0.5 bg-gray-700/80 text-green-400 text-[10px] rounded font-semibold">✓ Paid</span>
                                : <button
                                    onClick={() => setDoublesPayoutModal({ bowlers: b.bowler_ids })}
                                    className={`px-2 py-0.5 text-white text-[10px] rounded font-semibold transition-colors
                                      ${isWinner ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}`}>
                                    💰 Pay Pair
                                  </button>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()
              }
            </div>
          </div>
        ))}
        {payoutModal && (
          <BookiePayoutModal
            isOpen={true}
            onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId}
            bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id}
            sideEventType='side_pots'
            leagueId={sideEvent.league_id}
            defaultAmount={0}
            onPayoutComplete={() => { setPayoutModal(null); loadPaidBowlers(); }}
          />
        )}
        {doublesPayoutModal && (
          <DoublesPayoutModal
            bowlers={doublesPayoutModal.bowlers}
            sideEventId={sideEvent.id}
            leagueId={sideEvent.league_id}
            onClose={() => setDoublesPayoutModal(null)}
            onComplete={() => { setDoublesPayoutModal(null); loadPaidBowlers(); }}
          />
        )}
      </div>
    );
  }

  // ── HIGH GAME / HIGH SERIES ────────────────────────────────────────────────
  if (sideEvent.type === 'high_game' || sideEvent.type === 'high_series') {
    const isHighSeries = sideEvent.type === 'high_series';
    const standings: any[] = seResult?.standings || [];
    const winnerScore = standings.length > 0
      ? (isHighSeries ? standings[0].total_score : standings[0].best_game)
      : null;
    return (
      <div className="mt-6 border-t border-gray-700 pt-5">
        <SideEventPayoutPresets
          sideEvent={sideEvent}
          leagueId={sideEvent.league_id}
          seResult={seResult}
          presetsApiBase={`${API}/bookie-league-management/side-events/${sideEvent.id}`}
          paidBowlerIds={paidBowlerIds}
          onPayoutComplete={() => { loadData(); loadPaidBowlers(); }}
        />
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-amber-300">
            {isHighSeries ? 'High Series' : 'High Game'} Leaderboard
          </h4>
          <button onClick={toggleHandicap}
            className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${useHandicap ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
            Hdcp {useHandicap ? 'ON' : 'OFF'}
          </button>
        </div>
        {standings.length === 0
          ? <p className="text-xs text-gray-500 text-center py-4">No scores entered yet</p>
          : <div className="bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">
              {standings.map((s: any, i: number) => {
                const displayScore = isHighSeries ? s.total_score : s.best_game;
                const isWinner = i === 0;
                const isTied = !isWinner && winnerScore !== null && displayScore === winnerScore;
                return (
                  <div key={s.bowler_id} className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0
                    ${isWinner ? 'bg-yellow-900/20 text-yellow-200' : isTied ? 'bg-orange-900/20 text-orange-200' : 'text-gray-400'}`}>
                    <div className="flex items-center gap-1.5">
                      <span>{i + 1}. {s.bowler_name}</span>
                      {isTied && <span className="text-[9px] bg-orange-700/60 text-orange-200 px-1.5 py-0.5 rounded-full font-semibold">TIE</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-bold ${isWinner ? 'text-yellow-300' : isTied ? 'text-orange-300' : ''}`}>
                        {displayScore ?? '—'}{isHighSeries ? ' ser' : ''}
                      </span>
                      {i < 5 && (
                        paidBowlerIds.has(s.bowler_id)
                          ? <span className="px-2 py-0.5 bg-gray-700/80 text-green-400 text-[10px] rounded font-semibold">✓ Paid</span>
                          : <button
                              onClick={() => setPayoutModal({ bowlerId: s.bowler_id, bowlerName: s.bowler_name })}
                              className={`px-2 py-0.5 text-white text-[10px] rounded font-semibold transition-colors
                                ${isWinner ? 'bg-green-600 hover:bg-green-700' : isTied ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-600 hover:bg-gray-500'}`}>
                              💰 Pay Out
                            </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
        }
        {payoutModal && (
          <BookiePayoutModal
            isOpen={true}
            onClose={() => setPayoutModal(null)}
            bowlerId={payoutModal.bowlerId}
            bowlerName={payoutModal.bowlerName}
            sideEventId={sideEvent.id}
            sideEventType={sideEvent.type as 'high_game' | 'high_series'}
            leagueId={sideEvent.league_id}
            defaultAmount={0}
            onPayoutComplete={() => { setPayoutModal(null); loadPaidBowlers(); }}
          />
        )}
      </div>
    );
  }

  return null;
};

/* ─── Add Squad Modal ──────────────────────────────────────────────────── */
const AddSquadModal: React.FC<{ leagueId: number; onClose: () => void; onAdded: (squad: Squad) => void }> =
  ({ leagueId, onClose, onAdded }) => {
    const [date, setDate] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const submit = async () => {
      if (!date) { setErr('Please pick a date.'); return; }
      setSaving(true); setErr('');
      try {
        const r = await axios.post(
          `${API}/bookie-league-management/leagues/${leagueId}/squads`,
          { date },
          { headers: authHeader() }
        );
        onAdded({ id: r.data.squadId, name: r.data.name, date: r.data.date });
      } catch (e: any) {
        setErr(e?.response?.data?.error || 'Failed to add squad');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-sm p-6">
          <h3 className="font-bold text-white mb-4">Add New Squad</h3>
          <label className="block text-xs text-gray-400 mb-1">Squad Date</label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm mb-1"
          />
          <p className="text-xs text-gray-500 mb-4">Squad will be named automatically (e.g. Week N). The league end date will be extended if this date is beyond it.</p>
          {err && <p className="text-red-400 text-xs mb-3">{err}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-sm rounded-lg text-white">Cancel</button>
            <button onClick={submit} disabled={saving}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-sm rounded-lg text-white disabled:opacity-50">
              {saving ? 'Adding…' : 'Add Squad'}
            </button>
          </div>
        </div>
      </div>
    );
  };

/* ─── Main BookieLeagueManager Component ───────────────────────────────── */
const BookieLeagueManager: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [league, setLeague] = useState<League | null>(null);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [sideEvents, setSideEvents] = useState<SideEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedSquadId, setSelectedSquadId] = useState<number | null>(null);
  const [selectedSEId, setSelectedSEId] = useState<number | null>(null);
  const seTabBarRef = React.useRef<HTMLDivElement>(null);
  const scrollSETabs = (dir: 'left' | 'right') => {
    seTabBarRef.current?.scrollBy({ left: dir === 'right' ? 220 : -220, behavior: 'smooth' });
  };

  // Modal states
  const [showAddSE, setShowAddSE] = useState(false);
  const [addSEForSquad, setAddSEForSquad] = useState<number | null>(null); // null = all squads
  const [showScoreEntry, setShowScoreEntry] = useState(false);
  const [showFinancials, setShowFinancials] = useState(false);
  const [financialsSquadId, setFinancialsSquadId] = useState<number | null>(null);
  const [showAddSquad, setShowAddSquad] = useState(false);
  const [squadDeleteError, setSquadDeleteError] = useState<string | null>(null);
  const [deletingSquadId, setDeletingSquadId] = useState<number | null>(null);
  const [showHandicaps, setShowHandicaps] = useState(false);
  const [showQuickEntries, setShowQuickEntries] = useState(false);
  const [showQuickBracketEntries, setShowQuickBracketEntries] = useState(false);

  // ── Helper: open quick-entry windows in a new popup (split-screen friendly) ──
  const openQuickEntriesWindow = () => {
    if (!selectedSquadId || !leagueId) return;
    window.open(
      `/bookie/quick-entries/${leagueId}/${selectedSquadId}`,
      `quick-entries-${leagueId}-${selectedSquadId}`,
      'width=1280,height=860,top=80,left=80'
    );
  };

  const openQuickBracketEntriesWindow = () => {
    if (!selectedSquadId || !leagueId) return;
    window.open(
      `/bookie/quick-bracket-entries/${leagueId}/${selectedSquadId}`,
      `quick-bracket-entries-${leagueId}-${selectedSquadId}`,
      'width=1280,height=860,top=80,left=800'
    );
  };
  const [deletingSEId, setDeletingSEId] = useState<number | null>(null);
  const [deleteSEError, setDeleteSEError] = useState<string | null>(null);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [showAddBowlerModal, setShowAddBowlerModal] = useState(false);
  const [editSidePotId, setEditSidePotId] = useState<number | null>(null);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffData, setStaffData] = useState<{ ownerId: number; staffIds: number[]; bookies: { id: number; name: string; email: string }[] } | null>(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffSaving, setStaffSaving] = useState<number | null>(null);

  const openStaffModal = async () => {
    setShowStaffModal(true);
    setStaffLoading(true);
    try {
      const r = await axios.get(`${API}/bookie-league-management/leagues/${leagueId}/staff`, { headers: authHeader() });
      setStaffData({ ...r.data, staffIds: Array.isArray(r.data.staffIds) ? r.data.staffIds : Object.values(r.data.staffIds || {}) });
    } catch { setStaffData(null); }
    finally { setStaffLoading(false); }
  };

  const toggleStaff = async (adminId: number, isStaff: boolean) => {
    setStaffSaving(adminId);
    try {
      const r = await axios.patch(`${API}/bookie-league-management/leagues/${leagueId}/staff`,
        { adminId, action: isStaff ? 'remove' : 'add' }, { headers: authHeader() });
      setStaffData(prev => prev ? { ...prev, staffIds: r.data.staffIds } : prev);
    } catch { }
    finally { setStaffSaving(null); }
  };

  // Refresh counter to re-render entries panels when needed
  const [refreshKey, setRefreshKey] = useState(0);

  const loadLeague = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/bookie-league-management/leagues/${leagueId}`, { headers: authHeader() })
      .then(r => {
        setLeague(r.data.league);
        setSquads(r.data.squads || []);
        setSideEvents(r.data.sideEvents || []);
        if (r.data.squads?.length > 0 && !selectedSquadId) {
          setSelectedSquadId(r.data.squads[0].id);
        }
      })
      .catch(() => setError(t('bookie.manager.loadError', 'Failed to load league')))
      .finally(() => setLoading(false));
  }, [leagueId, selectedSquadId, t]);

  useEffect(() => { loadLeague(); }, [leagueId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Listen for refresh broadcasts from popup quick-entry windows ───────────
  useEffect(() => {
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel('bookie-entries-refresh');
      ch.onmessage = () => { loadLeague(); setRefreshKey(k => k + 1); };
    } catch { /* BroadcastChannel not available */ }
    return () => { ch?.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first SE for selected squad — preserves tab when entries are refreshed
  useEffect(() => {
    if (selectedSquadId) {
      const squadSEs = sideEvents.filter(se => se.squad_id === selectedSquadId);
      const currentIsValid = squadSEs.some(se => se.id === selectedSEId);
      if (!currentIsValid) {
        setSelectedSEId(squadSEs.length > 0 ? squadSEs[0].id : null);
      }
    }
  }, [selectedSquadId, sideEvents, selectedSEId]);

  const currentSquad = squads.find(s => s.id === selectedSquadId);
  const currentSquadSEs = sideEvents.filter(se => se.squad_id === selectedSquadId);
  const currentSE = currentSquadSEs.find(se => se.id === selectedSEId);

  const handleAddSEDone = () => {
    setShowAddSE(false);
    loadLeague();
    setRefreshKey(k => k + 1);
  };

  const handleEntriesRefresh = () => {
    setRefreshKey(k => k + 1);
    // Reload to update entry_count badge
    axios.get(`${API}/bookie-league-management/leagues/${leagueId}`, { headers: authHeader() })
      .then(r => setSideEvents(r.data.sideEvents || []))
      .catch(() => {});
  };

  const openAllFinancials = () => { setFinancialsSquadId(null); setShowFinancials(true); };
  const openSquadFinancials = () => { setFinancialsSquadId(selectedSquadId); setShowFinancials(true); };

  const toggleLeagueVisibility = async () => {
    if (!league) return;
    setTogglingVisibility(true);
    const newHidden = league.hidden === 1 ? 0 : 1;
    try {
      await axios.put(`${API}/bookie-league-management/leagues/${leagueId}/visibility`,
        { hidden: newHidden }, { headers: authHeader() });
      setLeague(prev => prev ? { ...prev, hidden: newHidden } : prev);
    } catch { } finally { setTogglingVisibility(false); }
  };

  const handleDeleteSE = async (se: SideEvent) => {
    if (!window.confirm(`Delete "${se.name}"? This cannot be undone.`)) return;
    setDeletingSEId(se.id);
    setDeleteSEError(null);
    try {
      await axios.delete(
        `${API}/bookie-league-management/side-events/${se.id}`,
        { headers: authHeader() }
      );
      // If the deleted SE was selected, clear selection before reloading
      if (selectedSEId === se.id) setSelectedSEId(null);
      loadLeague();
    } catch (e: any) {
      setDeleteSEError(e?.response?.data?.error || 'Failed to delete side event');
    } finally {
      setDeletingSEId(null);
    }
  };

  const handleSquadAdded = (newSquad: Squad) => {
    setSquads(prev => [...prev].sort((a, b) => a.date.localeCompare(b.date)).concat([newSquad]).sort((a, b) => a.date.localeCompare(b.date)));
    setShowAddSquad(false);
    loadLeague(); // reload to get fresh squad + end_date
  };

  const handleDeleteSquad = async (squadId: number) => {
    setDeletingSquadId(squadId);
    setSquadDeleteError(null);
    try {
      await axios.delete(
        `${API}/bookie-league-management/leagues/${leagueId}/squads/${squadId}`,
        { headers: authHeader() }
      );
      if (selectedSquadId === squadId) setSelectedSquadId(null);
      loadLeague();
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Failed to delete squad';
      setSquadDeleteError(msg);
    } finally {
      setDeletingSquadId(null);
    }
  };

  const handlePrintAllCashers = async () => {
    if (!league) return;
    try {
      // Fetch results for every squad
      const allResults: any[] = [];
      for (const squad of squads) {
        const r = await axios.get(`${API}/bookie-scores/squads/${squad.id}/side-event-results`, { headers: authHeader() });
        for (const se of (r.data || [])) {
          allResults.push({ ...se, _squad: squad.name });
        }
      }
      const payRes = await axios.get(`${API}/bookie-league-management/leagues/${leagueId}/payouts`, { headers: authHeader() });
      const paidMap = new Map<number, Set<number>>();
      for (const p of ((payRes.data?.payouts || []) as { side_event_id: number; recipient_id: number }[])) {
        if (!paidMap.has(p.side_event_id)) paidMap.set(p.side_event_id, new Set());
        paidMap.get(p.side_event_id)!.add(p.recipient_id);
      }
      printWinners(league.name, 'All Squads', allResults, paidMap);
    } catch { }
  };

  const handlePrintSquadCashers = async () => {
    if (!league || !selectedSquadId) return;
    try {
      const [r, payRes] = await Promise.all([
        axios.get(`${API}/bookie-scores/squads/${selectedSquadId}/side-event-results`, { headers: authHeader() }),
        axios.get(`${API}/bookie-league-management/squads/${selectedSquadId}/payouts`, { headers: authHeader() }),
      ]);
      const paidMap = new Map<number, Set<number>>();
      for (const p of ((payRes.data?.payouts || []) as { side_event_id: number; recipient_id: number }[])) {
        if (!paidMap.has(p.side_event_id)) paidMap.set(p.side_event_id, new Set());
        paidMap.get(p.side_event_id)!.add(p.recipient_id);
      }
      const squadLabel = currentSquad ? `${currentSquad.name} (${fmtDate(currentSquad.date)})` : 'Squad';
      printWinners(league.name, squadLabel, r.data || [], paidMap);
    } catch { }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !league) return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-400 mb-4">{error || 'League not found'}</p>
        <button onClick={() => navigate('/bookie/manage-league')} className="px-5 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm">
          {t('common.back', 'Go Back')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* ── TOP HEADER ──────────────────────────────────────────────── */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/bookie/manage-league')}
              className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors flex-shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="font-bold text-lg text-white leading-tight">{league.name}</h1>
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                {league.venue} · {fmtDate(league.start_date)} – {fmtDate(league.end_date)}
              </p>
            </div>
          </div>
          {/* League-wide action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => { setAddSEForSquad(null); setShowAddSE(true); }}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('bookie.manager.addAllSquads', 'Add SE (All)')}
            </button>
            <button onClick={() => navigate(`/bookie/manage-league/${leagueId}/bowler-groups`)}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Bowlers
            </button>
            <button onClick={openAllFinancials}
              className="px-3 py-1.5 bg-green-700 hover:bg-green-600 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('bookie.manager.financialsAll', 'Financials (All)')}
            </button>
            <button onClick={() => setShowHandicaps(true)}
              className="px-3 py-1.5 bg-teal-700 hover:bg-teal-600 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Set Handicaps & Info
            </button>
            <button onClick={handlePrintAllCashers}
              className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {t('bookie.manager.printAll', 'Print Results (All)')}
            </button>
            <button onClick={toggleLeagueVisibility} disabled={togglingVisibility}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50 ${league.hidden === 1 ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'bg-gray-600 hover:bg-gray-500 text-gray-200'}`}>
              {togglingVisibility
                ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : league.hidden === 1
                  ? <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
              }
              {league.hidden === 1 ? 'Unhide League' : 'Hide League'}
            </button>
            <button onClick={openStaffModal}
              className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-gray-900 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Add Staff
            </button>
          </div>
        </div>
      </div>

      {/* ── ADD STAFF MODAL ─────────────────────────────────────────── */}
      {showStaffModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-md shadow-2xl flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
              <h3 className="text-base font-semibold text-white">League Staff</h3>
              <button onClick={() => setShowStaffModal(false)} className="text-gray-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {staffLoading ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" /></div>
              ) : !staffData ? (
                <p className="text-red-400 text-sm text-center py-4">Failed to load staff data.</p>
              ) : staffData.bookies.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-4">No other bookies found in the system.</p>
              ) : (
                <ul className="space-y-2">
                  {staffData.bookies.filter(b => b.id !== staffData.ownerId).map(b => {
                    const isStaff = staffData.staffIds.includes(b.id);
                    const saving = staffSaving === b.id;
                    return (
                      <li key={b.id} className="flex items-center justify-between gap-3 py-2 border-b border-gray-800 last:border-0">
                        <div>
                          <p className="text-sm text-white font-medium">{b.name}</p>
                          <p className="text-xs text-gray-500">{b.email}</p>
                        </div>
                        <button
                          onClick={() => toggleStaff(b.id, isStaff)}
                          disabled={saving}
                          className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 ${
                            isStaff
                              ? 'bg-red-700 hover:bg-red-600 text-white'
                              : 'bg-yellow-500 hover:bg-yellow-400 text-gray-900'
                          }`}>
                          {saving ? '…' : isStaff ? 'Remove' : 'Add'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-700">
              <button onClick={() => setShowStaffModal(false)}
                className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── SQUAD TABS ──────────────────────────────────────────────── */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6">
        <div className="flex items-center overflow-x-auto scrollbar-hide gap-1 py-2" style={{ scrollbarWidth: 'none' }}>
          {squads.map(squad => (
            <div key={squad.id} className="relative flex-shrink-0 group">
              <button
                onClick={() => setSelectedSquadId(squad.id)}
                className={`flex-shrink-0 pl-3 pr-7 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap
                  ${selectedSquadId === squad.id
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}>
                <div>{squad.name}</div>
                <div className="text-xs opacity-75">{fmtDate(squad.date)}</div>
              </button>
              {/* × remove button */}
              <button
                onClick={e => { e.stopPropagation(); if (window.confirm(`Delete squad "${squad.name}"? This cannot be undone.`)) handleDeleteSquad(squad.id); }}
                disabled={deletingSquadId === squad.id}
                title="Remove squad"
                className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full text-gray-500 hover:text-red-400 hover:bg-red-900/40 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] leading-none disabled:opacity-30"
              >
                ✕
              </button>
            </div>
          ))}
          {/* + add squad button */}
          <button
            onClick={() => setShowAddSquad(true)}
            title="Add squad"
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-lg ml-1"
          >
            +
          </button>
        </div>
        {/* squad delete error banner */}
        {squadDeleteError && (
          <div className="pb-2 flex items-center gap-2">
            <p className="text-xs text-red-400 flex-1">{squadDeleteError}</p>
            <button onClick={() => setSquadDeleteError(null)} className="text-gray-500 hover:text-white text-xs">✕</button>
          </div>
        )}
      </div>

      {/* ── SQUAD BODY ──────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {squads.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <p>{t('bookie.manager.noSquads', 'No squads in this league.')}</p>
          </div>
        ) : !selectedSquadId ? null : (
          <>
            {/* Per-squad action bar */}
            <div className="bg-gray-750 border-b border-gray-700 px-4 sm:px-6 py-2.5 flex items-center gap-2 flex-wrap" style={{ backgroundColor: '#1e2433' }}>
              <span className="text-xs text-gray-500 mr-1">{currentSquad?.name}:</span>
              <button onClick={() => { setAddSEForSquad(selectedSquadId); setShowAddSE(true); }}
                className="px-2.5 py-1 bg-purple-600/80 hover:bg-purple-600 text-xs rounded-md text-white flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('bookie.manager.addThisSquad', 'Add SE')}
              </button>
              <button onClick={openSquadFinancials}
                className="px-2.5 py-1 bg-green-700/80 hover:bg-green-700 text-xs rounded-md text-white flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2" />
                </svg>
                {t('bookie.manager.financialsSquad', 'Financials')}
              </button>
              <button disabled={!selectedSquadId}
                onClick={() => setShowScoreEntry(true)}
                className="px-2.5 py-1 bg-indigo-700 hover:bg-indigo-600 text-xs rounded-md text-white flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {t('bookie.manager.enterScores', 'Enter Scores')}
              </button>
              <button
                disabled={!selectedSquadId || currentSquadSEs.length === 0}
                onClick={openQuickEntriesWindow}
                className="px-2.5 py-1 bg-teal-700 hover:bg-teal-600 text-xs rounded-md text-white flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Quick Entries
              </button>
              <button
                disabled={!selectedSquadId || currentSquadSEs.filter(se => se.type === 'bracket').length === 0}
                onClick={openQuickBracketEntriesWindow}
                className="px-2.5 py-1 bg-purple-800 hover:bg-purple-700 text-xs rounded-md text-white flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Quick Bracket Entries
              </button>
              <button onClick={handlePrintSquadCashers}
                className="px-2.5 py-1 bg-blue-700/80 hover:bg-blue-700 text-xs rounded-md text-white flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
                </svg>
                {t('bookie.manager.printSquad', 'Print Results (This Squad)')}
              </button>
              <button onClick={() => setShowAddBowlerModal(true)}
                className="px-2.5 py-1 bg-emerald-700/80 hover:bg-emerald-700 text-xs rounded-md text-white flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                + New Bowler to System
              </button>
            </div>

            {/* Side event tabs */}
            {currentSquadSEs.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="text-4xl mb-3">🎳</div>
                <p className="text-gray-400 mb-4">{t('bookie.manager.noSideEvents', 'No side events for this squad yet')}</p>
                <button onClick={() => { setAddSEForSquad(selectedSquadId); setShowAddSE(true); }}
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium">
                  {t('bookie.manager.addFirstSE', 'Add First Side Event')}
                </button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* SE tab bar */}
                <div className="relative border-b border-gray-700 flex items-stretch" style={{ backgroundColor: '#161e2e' }}>
                  <button onClick={() => scrollSETabs('left')} className="flex-shrink-0 px-1.5 text-gray-500 hover:text-gray-200 hover:bg-gray-700/50 transition-colors border-r border-gray-700/60 z-10">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <div ref={seTabBarRef} className="flex overflow-x-auto px-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' } as React.CSSProperties}>
                    {currentSquadSEs.map(se => (
                      <button key={se.id} onClick={() => setSelectedSEId(se.id)}
                        className={`flex-shrink-0 px-4 py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap
                          ${selectedSEId === se.id
                            ? 'border-purple-500 text-purple-300'
                            : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
                        {se.name}
                        {se.entry_count > 0 && (
                          <span className="ml-1.5 bg-purple-700 text-purple-100 text-[10px] px-1.5 py-0.5 rounded-full">
                            {se.type === 'custom' ? Math.round(Math.sqrt(se.entry_count)) : se.entry_count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => scrollSETabs('right')} className="flex-shrink-0 px-1.5 text-gray-500 hover:text-gray-200 hover:bg-gray-700/50 transition-colors border-l border-gray-700/60 z-10">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>

                {/* SE content */}
                {currentSE && (
                  <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {/* SE Header */}
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h3 className="font-bold text-white">{currentSE.name}</h3>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                          <span className="bg-purple-900/40 text-purple-300 px-2 py-0.5 rounded-full">
                            {TYPE_LABELS[currentSE.type] || currentSE.type}
                          </span>
                          <span className="bg-gray-700 px-2 py-0.5 rounded-full">
                            {currentSE.applies_to === 'doubles' ? t('bookie.addSE.applyDoubles', 'Doubles') : t('bookie.addSE.applyBowler', 'Bowler')}
                          </span>
                          {Number(currentSE.cost) > 0 && (
                            <span className="bg-green-900/40 text-green-300 px-2 py-0.5 rounded-full">
                              ${Number(currentSE.cost).toFixed(2)}
                            </span>
                          )}
                          {currentSE.applies_to_game_numbers && (
                            <span className="text-gray-500">G {currentSE.applies_to_game_numbers}</span>
                          )}
                        </div>
                      </div>
                      {/* Action buttons: Edit Config (side_pots / custom) + Delete (empty only) */}
                      <div className="flex items-center gap-2">
                        {(currentSE.type === 'side_pots' || currentSE.type === 'custom') && (
                          <button
                            onClick={() => setEditSidePotId(currentSE.id)}
                            title="Edit side pot configuration"
                            className="px-2.5 py-1.5 bg-blue-900/40 hover:bg-blue-700/70 border border-blue-700/50 text-blue-400 hover:text-white text-xs rounded-lg flex items-center gap-1.5 transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit Config
                          </button>
                        )}
                        {currentSE.entry_count === 0 && (
                          <button
                            onClick={() => handleDeleteSE(currentSE)}
                            disabled={deletingSEId === currentSE.id}
                            title="Delete this side event (no entries)"
                            className="px-2.5 py-1.5 bg-red-900/40 hover:bg-red-700/70 border border-red-700/50 text-red-400 hover:text-white text-xs rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-40">
                            {deletingSEId === currentSE.id
                              ? <div className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                              : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            }
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    {deleteSEError && (
                      <div className="mb-4 px-3 py-2 bg-red-900/40 border border-red-500/40 rounded-lg text-red-300 text-xs flex items-center justify-between">
                        <span>{deleteSEError}</span>
                        <button onClick={() => setDeleteSEError(null)} className="text-red-400 hover:text-white ml-2">✕</button>
                      </div>
                    )}

                    {/* Entries / Bracket / Clean Game / Doubles panel */}
                    {currentSE.type === 'clean_game'
                      ? <CleanGamePanel key={`${currentSE.id}-${refreshKey}`} sideEvent={currentSE} leagueId={Number(leagueId)} onRefresh={handleEntriesRefresh} />
                      : currentSE.type === 'bracket'
                        ? <BracketEntriesPanel key={`${currentSE.id}-${refreshKey}`} sideEvent={currentSE} leagueId={Number(leagueId)} onRefresh={handleEntriesRefresh} />
                        : currentSE.applies_to === 'doubles'
                          ? <DoublesPanel key={`${currentSE.id}-${refreshKey}`} sideEvent={currentSE} leagueId={Number(leagueId)} onRefresh={handleEntriesRefresh} />
                          : <EntriesPanel key={`${currentSE.id}-${refreshKey}`} sideEvent={currentSE} leagueId={Number(leagueId)} onRefresh={handleEntriesRefresh} />
                    }
                    {/* Live data panel for types that have real-time results */}
                    {['survivor', 'eliminator', 'mystery_score', 'mystery_series', 'side_pots', 'custom', 'high_game', 'high_series'].includes(currentSE.type) && (
                      <SELivePanel key={`live-${currentSE.id}`} sideEvent={currentSE} squadId={selectedSquadId} refreshKey={refreshKey} />
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── MODALS ──────────────────────────────────────────────────── */}
      {showAddSE && (
        <BookieAddSideEventModal
          leagueId={Number(leagueId)}
          squadId={addSEForSquad}
          onClose={() => setShowAddSE(false)}
          onAdded={handleAddSEDone}
        />
      )}
      {showFinancials && league && (
        <FinancialsPanel
          leagueId={Number(leagueId)}
          squadId={financialsSquadId}
          leagueName={league.name}
          squadName={financialsSquadId ? squads.find(s => s.id === financialsSquadId)?.name : undefined}
          onClose={() => setShowFinancials(false)}
        />
      )}
      {showScoreEntry && selectedSquadId && currentSquad && (
        <BookieScoreEntry
          squadId={selectedSquadId}
          squadName={currentSquad.name}
          squadDate={currentSquad.date}
          leagueId={Number(leagueId)}
          onClose={() => setShowScoreEntry(false)}
        />
      )}
      {showAddSquad && (
        <AddSquadModal
          leagueId={Number(leagueId)}
          onClose={() => setShowAddSquad(false)}
          onAdded={handleSquadAdded}
        />
      )}
      {showHandicaps && league && (
        <BookieHandicapsModal
          leagueId={Number(leagueId)}
          leagueName={league.name}
          onClose={() => setShowHandicaps(false)}
        />
      )}
      {showAddBowlerModal && (
        <AddBowlerModal onClose={() => setShowAddBowlerModal(false)} />
      )}
      {editSidePotId !== null && (
        <BookieEditSidePotModal
          sideEventId={editSidePotId}
          onClose={() => setEditSidePotId(null)}
          onSaved={() => { setEditSidePotId(null); loadLeague(); }}
        />
      )}
    </div>
  );
};

export default BookieLeagueManager;
